import type { AdminClient } from "../api.js";
import {
  ConsoleDataError,
  MONTH_NOT_LOADED_TEXT,
  notPublishedReason,
} from "../console/data-source.js";
import {
  balanceSheetLineLabel,
  cashFlowGapCents,
  composeByLocation,
  composeCashFlow,
  composePnl,
  destroyCharts,
  formatShortDate,
  missingComparisonMonthSentence,
  missingMonthSentence,
  NO_COMPARISON_LINES_TEXT,
  provisionalNoticeOf,
  renderReport,
  reportIsEmpty,
  shownTable,
  slugText,
  statementPeriods,
  tableCsv,
  type Chart,
  type ReportCompare,
  type ShownTable,
} from "../console/report-render.js";
import {
  compareMonths,
  customRangeMonths,
  monthEndOf,
  type CompareBasis,
  type CompareChoice,
} from "../console/report-scope.js";
import { REPORT_IDS, type ReportId } from "../console/types.js";
import type {
  AdhocWindowEnvelope,
  BalanceSheetData,
  ByLocationData,
  CashFlowData,
  LedgerAccountsResponse,
  LedgerNotPublishedReason,
  LedgerStatement,
  LedgerStatementReport,
  PnlData,
  PyComparisonData,
  ReportDataMap,
  ReportSnapshot,
  ReportWindowCatalogItem,
} from "../console/types.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { clear, h } from "../dom.js";
import {
  accountLabel,
  companyNameText,
  dateText,
  lineLabelText,
  monthText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navigateTo } from "../ui/nav-bus.js";
import { readerRole } from "../ui/facts.js";
import { advice, can } from "../ui/capabilities.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { runWrite, type WriteOutcome } from "../ui/write.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { BOOKS_VIEW_OFF_TEXT } from "./general-ledger.js";
import type { CompanySlug, StaffRole } from "../types.js";
import {
  defaultPeriod,
  formatPeriodLabel,
  isValidPeriod,
  shiftPeriod,
} from "../ui/period-control.js";

export interface ReportsPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /**
   * The reader's role, as the shell holds it (owner decision 10: an unknown
   * role reads as the account manager, never the owner).
   */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1). The statement lines, their CSV and
   * the printed PDF all name an account.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

/**
 * Prior-year availability is a company fact, so one probe per client is
 * enough. Caching it on the client keeps a tab tour from paying the probe on
 * every Reports visit, while a fresh client (a new page session) probes again.
 */
const pyAvailabilityByClient = new WeakMap<AdminClient, Map<string, boolean>>();

/**
 * The monthly statements one Reports page session has already read (rd15 R1
 * fix rounds 1 and 2). A year to date with a comparison is 24 reads of the
 * Worker's monthly statement — 48 if a month falls back from the saved
 * statement to the live one — and the admin route is capped at 60 requests a
 * minute per IP. The same months are read again on every tab switch and every
 * comparison change, so the page keeps what it read.
 *
 * THE MEMORY IS ONE VISIT LONG. It lives in the page's own closure, so opening
 * Reports again starts empty: a booking approved in To review is on the
 * statement when the person comes back, and a figure on screen is never older
 * than the caption beside it says. The same closure is what a landed
 * report-window request and the caption's Refresh empty.
 *
 * Keyed by company + report + month + which source answered (the saved
 * statement or the live ledger), so a value is never served for another
 * question. A success is kept; so is a month nobody has published, which costs
 * two requests and can only answer the same way for the rest of the visit. A
 * read that FAILED is never kept — that is the one that may change.
 */
const MONTH_CACHE_LIMIT = 240;

const REPORT_LABELS: Record<ReportId, string> = {
  pnl: "Profit & Loss",
  "balance-sheet": "Balance Sheet",
  "cash-flow": "Cash Flow",
  "by-location": "By Location",
  "py-comparison": "PY Comparison",
};

const REPORT_LANDMARK: Record<ReportId, string> = {
  pnl: "Profit & Loss",
  "balance-sheet": "Balance Sheet",
  "cash-flow": "Cash Flow",
  "by-location": "By Location",
  "py-comparison": "Prior Year Comparison",
};

const STATEMENT_TITLES: Record<ReportId, string> = {
  pnl: "Statement of Profit and Loss",
  "balance-sheet": "Balance Sheet",
  "cash-flow": "Statement of Cash Flows",
  "by-location": "Income and Expenses by Location",
  "py-comparison": "Prior Year Comparison",
};

/**
 * How many monthly statements one scope may have in flight at once. Four keeps
 * a year to date inside the admin route's 60-a-minute ceiling while still
 * finishing in a few round trips; see `readScope`.
 */
const SCOPE_CONCURRENCY = 4;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/** Letter page, PDF points. */
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN_L = 54;
const MARGIN_R = 558;
const MARGIN_TOP = 40;
const MARGIN_BOTTOM = 48;
const HEADER_H = 88;
const FOOTER_H = 28;
const BODY_SIZE = 10;
const SMALL_SIZE = 8;
const LINE = 13;
const COL_W = 82;
const COURIER_EM = 0.6;

/**
 * Times-Roman glyph widths, WinAnsi 32–126, thousandths of an em.
 * Used only to wrap labels — never to reshape amounts.
 */
const TIMES_ROMAN_WIDTHS = [
  250, 333, 408, 500, 500, 833, 778, 180, 333, 333, 500, 564, 250, 333, 250, 278, 500, 500, 500,
  500, 500, 500, 500, 500, 500, 500, 278, 278, 564, 564, 564, 444, 921, 722, 667, 667, 722, 611,
  556, 722, 722, 333, 389, 722, 611, 889, 722, 722, 556, 722, 667, 556, 611, 722, 722, 944, 722,
  722, 611, 333, 278, 333, 469, 500, 333, 444, 500, 444, 500, 444, 333, 500, 500, 278, 278, 500,
  278, 778, 500, 500, 500, 500, 333, 389, 278, 500, 500, 722, 500, 500, 444, 480, 200, 480, 541,
];

export interface ReportPdfInput {
  company: CompanySlug;
  /**
   * The ONE account-label context (rd13 L1): ledger company segment -> the
   * company's plain name. A printed statement names the company in a
   * between-companies line, never its internal key.
   */
  companies: AccountLabelContext;
  report: ReportId;
  window: string;
  windowStart?: string;
  windowEnd?: string;
  generatedAt: string;
  comparisonWindow?: string;
  data: ReportDataMap[ReportId];
}

export interface ReportPdf {
  filename: string;
  bytes: Uint8Array;
}

function snapshotFromWindow<K extends ReportId>(
  envelope: AdhocWindowEnvelope<ReportDataMap[K]>,
  report: K,
): ReportSnapshot<ReportDataMap[K]> {
  return {
    apiVersion: "v1",
    // The snapshot is wire-shaped data: the slug crosses as a key, never as
    // words a person reads.
    company: slugText(envelope.company),
    report,
    period: envelope.window,
    generatedAt: envelope.generatedAt,
    pipelineRunId: "window",
    data: envelope.data,
  };
}

function saveDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function uint8ToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

function downloadBlob(body: string | Uint8Array, type: string): Blob {
  if (typeof body === "string") {
    return new Blob([body], { type });
  }
  return new Blob([uint8ToArrayBuffer(body)], { type });
}

function pdfInputFromEnvelope<K extends ReportId>(
  envelope: AdhocWindowEnvelope<ReportDataMap[K]>,
  report: K,
  companies: AccountLabelContext,
): ReportPdfInput {
  const input: ReportPdfInput = {
    company: envelope.company,
    companies,
    report,
    window: envelope.window,
    windowStart: envelope.windowStart,
    windowEnd: envelope.windowEnd,
    generatedAt: envelope.generatedAt,
    data: envelope.data,
  };
  if (envelope.comparisonWindow !== undefined) {
    input.comparisonWindow = envelope.comparisonWindow;
  }
  return input;
}

/** Filename carries company and window so a downloaded file names its own period. */
export function reportPdfFilename(
  company: CompanySlug,
  report: string,
  window: string,
): string {
  const safe = (value: string): string => {
    const cleaned = value.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
    return cleaned === "" ? "report" : cleaned;
  };
  return `${safe(slugText(company))}-${safe(report)}-${safe(window)}.pdf`;
}

/**
 * Print the artifact's amount spelling unchanged. No grouping, no $ prefix, no
 * accounting parentheses, no parseDecimal round-trip — if the JSON says
 * `1234.56`, the PDF says `1234.56`.
 */
export function statementAmountText(raw: string): string {
  return raw;
}

export function buildReportPdf(input: ReportPdfInput): ReportPdf {
  const streams = paginate(input);
  return {
    filename: reportPdfFilename(input.company, input.report, input.window),
    bytes: assemblePdf(streams),
  };
}

type FlowItem =
  | { kind: "space"; height: number }
  | { kind: "rule"; style: "single" | "double"; height: number }
  | { kind: "section"; text: string; height: number }
  | { kind: "note"; lines: string[]; height: number }
  | {
      kind: "row";
      labelLines: string[];
      detailLines: string[];
      amounts: string[];
      emphasis: "normal" | "total" | "headline";
      height: number;
    };

function formatCivilDate(iso: string): string {
  const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(iso);
  if (match === null) return iso;
  const year = match[1];
  const monthToken = match[2];
  const dayToken = match[3];
  if (year === undefined || monthToken === undefined || dayToken === undefined) return iso;
  const month = MONTHS[Number(monthToken) - 1];
  const dayNum = Number(dayToken);
  if (month === undefined || dayNum < 1 || dayNum > 31) return iso;
  return `${month} ${String(dayNum)}, ${year}`;
}

function coverageLine(input: ReportPdfInput): string {
  if (input.report === "balance-sheet") {
    const asOf = (input.data as BalanceSheetData).asOf;
    const date = input.windowEnd ?? asOf;
    return `As of ${formatCivilDate(date)}`;
  }
  const start = input.windowStart ?? periodBound(input.data, "start");
  const end = input.windowEnd ?? periodBound(input.data, "end");
  if (start !== undefined && end !== undefined) {
    return `For the period ${formatCivilDate(start)} through ${formatCivilDate(end)}`;
  }
  return `For ${windowText(input.window)}`;
}

/**
 * A report window token as a person reads it (rd Y3): the API and the URL keep
 * `2026-06` / `2026-Q2`; a printed statement says `June 2026` / `Q2 2026`.
 */
export function windowText(token: string): string {
  const clean = token.trim();
  if (clean === "") return "this period";
  if (/^\d{4}-\d{2}$/.test(clean)) return monthText(clean) || clean;
  const quarter = /^\d{4}-Q([1-4])$/.exec(clean);
  if (quarter !== null) {
    return `Q${quarter[1] ?? ""} ${clean.slice(0, 4)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return formatCivilDate(clean);
  if (clean.includes(":")) {
    // A saved range window is `start:end`, either two dates or two months.
    const [from = "", to = ""] = clean.split(":");
    return `${boundText(from)} to ${boundText(to)}`;
  }
  if (clean === "quarter") return "this quarter";
  if (clean === "year") return "this year";
  // An unknown shape is still contract text: it goes through the hazard strip
  // before it can reach an aria-label or a printed line.
  return amVisibleDisplayText(clean);
}

/** One end of a span: `2025-01` reads `January 2025`, a full date reads `Jan 1, 2025`. */
function boundText(value: string): string {
  if (/^\d{4}-\d{2}$/.test(value)) return monthText(value) || value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatCivilDate(value);
  return value;
}

function periodBound(data: ReportDataMap[ReportId], which: "start" | "end"): string | undefined {
  if (!("periodStart" in data) || !("periodEnd" in data)) return undefined;
  const value = which === "start" ? data.periodStart : data.periodEnd;
  return value;
}

function currencyOf(data: ReportDataMap[ReportId]): string {
  return data.currency;
}

function timesWidth(text: string, size: number): number {
  let width = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 63;
    const indexed = TIMES_ROMAN_WIDTHS[code - 32];
    const units = indexed === undefined || code < 32 || code > 126 ? 500 : indexed;
    width += (units / 1000) * size;
  }
  return width;
}

function wrapTimes(text: string, size: number, maxWidth: number): string[] {
  if (text === "") return [""];
  if (timesWidth(text, size) <= maxWidth) return [text];
  const words = text.split(/\s+/u);
  const lines: string[] = [];
  let current = "";
  const pushHard = (token: string): void => {
    let rest = token;
    while (rest.length > 0) {
      let take = rest.length;
      while (take > 1 && timesWidth(rest.slice(0, take), size) > maxWidth) take -= 1;
      lines.push(rest.slice(0, take));
      rest = rest.slice(take);
    }
  };
  for (const word of words) {
    const trial = current === "" ? word : `${current} ${word}`;
    if (timesWidth(trial, size) <= maxWidth) {
      current = trial;
      continue;
    }
    if (current !== "") lines.push(current);
    if (timesWidth(word, size) <= maxWidth) {
      current = word;
    } else {
      pushHard(word);
      current = "";
    }
  }
  if (current !== "") lines.push(current);
  return lines.length > 0 ? lines : [""];
}

function labelWidthFor(amountCount: number): number {
  const amountBand = Math.max(1, amountCount) * COL_W;
  return MARGIN_R - amountBand - 12 - MARGIN_L;
}

function rowHeight(labelLines: string[], detailLines: string[]): number {
  return Math.max(1, labelLines.length) * LINE + detailLines.length * 10 + 2;
}

function makeRow(
  label: string,
  amounts: readonly string[],
  options: {
    detail?: string;
    emphasis?: "normal" | "total" | "headline";
    amountCount?: number;
  } = {},
): FlowItem {
  const amountCount = options.amountCount ?? Math.max(1, amounts.length);
  const width = labelWidthFor(amountCount);
  const labelLines = wrapTimes(label, BODY_SIZE, width);
  const detailLines =
    options.detail !== undefined && options.detail !== ""
      ? wrapTimes(options.detail, SMALL_SIZE, width - 12)
      : [];
  return {
    kind: "row",
    labelLines,
    detailLines,
    amounts: amounts.map(statementAmountText),
    emphasis: options.emphasis ?? "normal",
    height: rowHeight(labelLines, detailLines),
  };
}

function makeNote(text: string, amountCount: number): FlowItem {
  const lines = wrapTimes(text, BODY_SIZE, labelWidthFor(amountCount) + amountCount * COL_W);
  return { kind: "note", lines, height: lines.length * LINE + 8 };
}

function layoutItems(input: ReportPdfInput): FlowItem[] {
  const items: FlowItem[] = [];
  switch (input.report) {
    case "pnl":
      layoutPnl(items, input.data as PnlData, input.companies);
      break;
    case "balance-sheet":
      layoutBalanceSheet(items, input.data as BalanceSheetData, input.companies);
      break;
    case "cash-flow":
      layoutCashFlow(items, input.data as CashFlowData);
      break;
    case "by-location":
      layoutByLocation(items, input.data as ByLocationData);
      break;
    case "py-comparison":
      layoutPyComparison(items, input.data as PyComparisonData);
      break;
    default: {
      const exhaustive: never = input.report;
      throw new Error(`Unknown report id: ${String(exhaustive)}`);
    }
  }
  return items;
}

function columnHeaders(input: ReportPdfInput): string[] {
  switch (input.report) {
    case "by-location":
      return ["Income", "Expenses", "Net"];
    case "py-comparison": {
      const data = input.data as PyComparisonData;
      // The column heads name the two periods the same way the header lines
      // do (rd Y3): the statement must not print the API token in one place
      // and "June 2026" in another.
      if (data.available) {
        return [windowText(input.window), windowText(data.priorPeriod)];
      }
      return ["Amount"];
    }
    default:
      return ["Amount"];
  }
}

function rowLabelHeader(report: ReportId): string {
  return report === "by-location" ? "Location" : "Line";
}

function layoutPnl(
  items: FlowItem[],
  data: PnlData,
  companies: AccountLabelContext,
): void {
  if (data.sections.length === 0) {
    items.push(makeNote("No line items for this period.", 1));
  }
  for (const section of data.sections) {
    items.push({ kind: "section", text: section.label, height: 20 });
    for (const line of section.lines) {
      items.push(
        makeRow(balanceSheetLineLabel(line.account, line.label), [line.amount], {
          // The PDF's own font set has no U+203A, so the statement uses the
          // ledger body's middot separator; either way the reader never sees a
          // colon path.
          detail: accountLabel(line.account, companies, { separator: " \u00b7 " }),
        }),
      );
    }
    items.push({ kind: "rule", style: "single", height: 10 });
    items.push(makeRow(`Total ${section.label}`, [section.total], { emphasis: "total" }));
    items.push({ kind: "space", height: 10 });
  }
  items.push({ kind: "rule", style: "double", height: 14 });
  items.push(makeRow("Net income", [data.netIncome], { emphasis: "headline" }));
  if (data.priorYearNetIncome !== null) {
    items.push(makeRow("Prior year net income", [data.priorYearNetIncome]));
  }
}

function layoutBalanceSheet(
  items: FlowItem[],
  data: BalanceSheetData,
  companies: AccountLabelContext,
): void {
  if (data.sections.length === 0) {
    items.push(makeNote("No line items for this period.", 1));
  }
  for (const section of data.sections) {
    items.push({ kind: "section", text: section.label, height: 20 });
    for (const line of section.lines) {
      items.push(
        makeRow(balanceSheetLineLabel(line.account, line.label), [line.amount], {
          // The PDF's own font set has no U+203A, so the statement uses the
          // ledger body's middot separator; either way the reader never sees a
          // colon path.
          detail: accountLabel(line.account, companies, { separator: " \u00b7 " }),
        }),
      );
    }
    items.push({ kind: "rule", style: "single", height: 10 });
    items.push(makeRow(`Total ${section.label}`, [section.total], { emphasis: "total" }));
    items.push({ kind: "space", height: 10 });
  }
}

function layoutCashFlow(items: FlowItem[], data: CashFlowData): void {
  items.push(makeRow("Opening cash", [data.openingCash], { emphasis: "headline" }));
  items.push({ kind: "space", height: 8 });
  if (data.sections.length === 0) {
    items.push(makeNote("No line items for this period.", 1));
  }
  for (const section of data.sections) {
    items.push({ kind: "section", text: section.label, height: 20 });
    for (const line of section.lines) {
      items.push(makeRow(lineLabelText(line.label), [line.amount]));
    }
    items.push({ kind: "rule", style: "single", height: 10 });
    items.push(makeRow(`Total ${section.label}`, [section.total], { emphasis: "total" }));
    items.push({ kind: "space", height: 10 });
  }
  items.push({ kind: "rule", style: "double", height: 14 });
  items.push(makeRow("Net change", [data.netChange], { emphasis: "headline" }));
  items.push(makeRow("Closing cash", [data.closingCash], { emphasis: "headline" }));
}

function layoutByLocation(items: FlowItem[], data: ByLocationData): void {
  const paintTable = (rows: readonly { location: string; income: string; expenses: string; net: string }[]): void => {
    for (const row of rows) {
      items.push(
        makeRow(amVisibleDisplayText(row.location), [row.income, row.expenses, row.net], {
          amountCount: 3,
        }),
      );
    }
  };
  if (data.locations.length === 0 && (data.parents ?? []).length === 0) {
    items.push(makeNote("No line items for this period.", 3));
    return;
  }
  if (data.locations.length > 0) {
    paintTable(data.locations);
  }
  const parents = data.parents ?? [];
  if (parents.length > 0) {
    items.push({ kind: "space", height: 12 });
    items.push({ kind: "section", text: "Parent roll-up", height: 20 });
    paintTable(parents);
  }
}

function layoutPyComparison(items: FlowItem[], data: PyComparisonData): void {
  if (!data.available) {
    const note =
      data.note !== undefined && data.note !== ""
        ? amVisibleDisplayText(data.note)
        : "Prior-year comparison is not available.";
    items.push(makeNote(note, 2));
    return;
  }
  if (data.lines.length === 0) {
    items.push(makeNote(NO_COMPARISON_LINES_TEXT, 2));
    return;
  }
  for (const line of data.lines) {
    items.push(
      makeRow(lineLabelText(line.label), [line.current, line.prior], { amountCount: 2 }),
    );
  }
}

function bodyBudget(): number {
  return PAGE_H - MARGIN_TOP - HEADER_H - MARGIN_BOTTOM - FOOTER_H;
}

function paginate(input: ReportPdfInput): string[] {
  const items = layoutItems(input);
  const budget = bodyBudget();
  const pages: FlowItem[][] = [];
  let current: FlowItem[] = [];
  let used = 0;

  const flush = (): void => {
    pages.push(current);
    current = [];
    used = 0;
  };

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (item === undefined) continue;
    let need = item.height;
    const next = items[index + 1];
    if (item.kind === "section" && next !== undefined) {
      need += next.height;
    }
    if (used + need > budget && current.length > 0) {
      flush();
    }
    current.push(item);
    used += item.height;
  }
  if (current.length > 0 || pages.length === 0) flush();

  const count = pages.length;
  return pages.map((pageItems, pageIndex) =>
    drawPage(input, pageItems, pageIndex + 1, count),
  );
}

function pdfLiteral(text: string): string {
  let out = "(";
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (char === "\\" || char === "(" || char === ")") {
      out += `\\${char}`;
    } else if (char === "\n") {
      out += "\\n";
    } else if (char === "\r") {
      out += "\\r";
    } else if (code === 0x2013 || code === 0x2014 || code === 0x2212) {
      out += "-";
    } else if (code >= 32 && code <= 126) {
      out += char;
    } else if (code >= 128 && code <= 255) {
      out += `\\${code.toString(8).padStart(3, "0")}`;
    } else {
      out += "?";
    }
  }
  return `${out})`;
}

function courierWidth(text: string, size: number): number {
  return text.length * size * COURIER_EM;
}

function amountX(text: string, colIndex: number, colCount: number, size: number): number {
  const rightEdge = MARGIN_R - (colCount - 1 - colIndex) * COL_W;
  return rightEdge - courierWidth(text, size);
}

class PageContent {
  private readonly ops: string[] = [];

  text(font: "F1" | "F2" | "F3", size: number, x: number, y: number, value: string): void {
    this.ops.push(
      "BT",
      `/${font} ${size.toFixed(1)} Tf`,
      `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm`,
      `${pdfLiteral(value)} Tj`,
      "ET",
    );
  }

  rule(y: number, style: "single" | "double"): void {
    this.ops.push(
      "0.4 w",
      `${String(MARGIN_L)} ${y.toFixed(2)} m ${String(MARGIN_R)} ${y.toFixed(2)} l S`,
    );
    if (style === "double") {
      const y2 = y - 2.2;
      this.ops.push(
        `${String(MARGIN_L)} ${y2.toFixed(2)} m ${String(MARGIN_R)} ${y2.toFixed(2)} l S`,
      );
    }
  }

  toStream(): string {
    return `${this.ops.join("\n")}\n`;
  }
}

function drawPage(
  input: ReportPdfInput,
  items: readonly FlowItem[],
  page: number,
  pageCount: number,
): string {
  const content = new PageContent();
  let y = PAGE_H - MARGIN_TOP;
  const continued = page > 1 ? " (continued)" : "";
  const currency = currencyOf(input.data);
  const currencyBit = currency === "" ? "" : ` · ${currency}`;

  // The contract carries the slug; a printed statement names the company the
  // way a person writes it, and says "this company" when no name is known
  // (rd-polish-rules §1: never the slug).
  content.text("F2", 16, MARGIN_L, y, companyNameText(slugText(input.company)));
  y -= 18;
  content.text("F2", 12, MARGIN_L, y, `${STATEMENT_TITLES[input.report]}${continued}`);
  y -= 16;
  content.text("F1", 10, MARGIN_L, y, coverageLine(input));
  y -= 13;
  content.text("F1", 9, MARGIN_L, y, `${windowText(input.window)}${currencyBit}`);
  y -= 12;
  if (input.comparisonWindow !== undefined && input.comparisonWindow !== "") {
    content.text("F1", 9, MARGIN_L, y, `Compared to ${windowText(input.comparisonWindow)}`);
  }
  y -= 10;
  content.rule(y, "single");
  y -= 14;
  const headers = columnHeaders(input);
  content.text("F1", SMALL_SIZE, MARGIN_L, y, rowLabelHeader(input.report));
  for (let col = 0; col < headers.length; col += 1) {
    const header = headers[col];
    if (header === undefined) continue;
    content.text(
      "F1",
      SMALL_SIZE,
      amountX(header, col, headers.length, SMALL_SIZE),
      y,
      header,
    );
  }

  y = PAGE_H - MARGIN_TOP - HEADER_H;
  for (const item of items) {
    if (item.kind === "space") {
      y -= item.height;
      continue;
    }
    if (item.kind === "rule") {
      content.rule(y - 4, item.style);
      y -= item.height;
      continue;
    }
    if (item.kind === "section") {
      content.text("F2", 11, MARGIN_L, y - 11, item.text);
      y -= item.height;
      continue;
    }
    if (item.kind === "note") {
      for (const line of item.lines) {
        content.text("F1", BODY_SIZE, MARGIN_L, y - 11, line);
        y -= LINE;
      }
      y -= 8;
      continue;
    }
    const font: "F1" | "F2" = item.emphasis === "normal" ? "F1" : "F2";
    const colCount = Math.max(1, item.amounts.length);
    for (let lineIndex = 0; lineIndex < item.labelLines.length; lineIndex += 1) {
      const line = item.labelLines[lineIndex];
      if (line === undefined) continue;
      content.text(font, BODY_SIZE, MARGIN_L, y - 11, line);
      if (lineIndex === 0) {
        for (let col = 0; col < item.amounts.length; col += 1) {
          const amount = item.amounts[col];
          if (amount === undefined) continue;
          content.text(
            "F3",
            BODY_SIZE,
            amountX(amount, col, colCount, BODY_SIZE),
            y - 11,
            amount,
          );
        }
      }
      y -= LINE;
    }
    for (const detail of item.detailLines) {
      content.text("F1", SMALL_SIZE, MARGIN_L + 12, y - 8, detail);
      y -= 10;
    }
    y -= 2;
  }

  const footerY = MARGIN_BOTTOM - 8;
  content.rule(footerY + 14, "single");
  const generated = dateText(input.generatedAt);
  if (generated !== "") {
    content.text("F1", 8, MARGIN_L, footerY, `Generated ${generated}`);
  }
  const pageLabel = `Page ${String(page)} of ${String(pageCount)}`;
  content.text("F1", 8, MARGIN_R - timesWidth(pageLabel, 8), footerY, pageLabel);
  content.text("F1", 8, MARGIN_L, footerY - 11, windowText(input.window));
  return content.toStream();
}

function assemblePdf(streams: readonly string[]): Uint8Array {
  if (streams.length === 0) {
    throw new Error("PDF has no pages");
  }
  const pageCount = streams.length;
  const fontF1 = 3 + 2 * pageCount;
  const fontF2 = fontF1 + 1;
  const fontF3 = fontF1 + 2;
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let size = 0;

  const add = (text: string): void => {
    for (let i = 0; i < text.length; i += 1) {
      if ((text.codePointAt(i) ?? 0) > 127) {
        throw new Error("PDF internal encoding is not ASCII");
      }
    }
    const bytes = encoder.encode(text);
    chunks.push(bytes);
    size += bytes.length;
  };

  const addObj = (text: string): void => {
    offsets.push(size);
    add(text);
  };

  add("%PDF-1.4\n");
  addObj("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
  const kids = streams.map((_, index) => `${String(3 + index)} 0 R`).join(" ");
  addObj(
    `2 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${String(pageCount)} /MediaBox [0 0 ${String(PAGE_W)} ${String(PAGE_H)}] >>\nendobj\n`,
  );
  for (let index = 0; index < pageCount; index += 1) {
    const contentNo = 3 + pageCount + index;
    addObj(
      `${String(3 + index)} 0 obj\n<< /Type /Page /Parent 2 0 R /Contents ${String(contentNo)} 0 R /Resources << /Font << /F1 ${String(fontF1)} 0 R /F2 ${String(fontF2)} 0 R /F3 ${String(fontF3)} 0 R >> >> >>\nendobj\n`,
    );
  }
  for (let index = 0; index < pageCount; index += 1) {
    const stream = streams[index];
    if (stream === undefined) throw new Error("missing content stream");
    addObj(
      `${String(3 + pageCount + index)} 0 obj\n<< /Length ${String(stream.length)} >>\nstream\n${stream}endstream\nendobj\n`,
    );
  }
  addObj(
    `${String(fontF1)} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Times-Roman /Encoding /WinAnsiEncoding >>\nendobj\n`,
  );
  addObj(
    `${String(fontF2)} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Times-Bold /Encoding /WinAnsiEncoding >>\nendobj\n`,
  );
  addObj(
    `${String(fontF3)} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>\nendobj\n`,
  );

  const xrefOffset = size;
  let xref = `xref\n0 ${String(offsets.length + 1)}\n0000000000 65535 f \n`;
  for (const offset of offsets) {
    xref += `${offset.toString().padStart(10, "0")} 00000 n \n`;
  }
  add(xref);
  add(
    `trailer\n<< /Size ${String(offsets.length + 1)} /Root 1 0 R >>\nstartxref\n${String(xrefOffset)}\n%%EOF\n`,
  );

  const out = new Uint8Array(size);
  let cursor = 0;
  for (const chunk of chunks) {
    out.set(chunk, cursor);
    cursor += chunk.length;
  }
  return out;
}

/** Financial statements — white paper only when a statement is drawn. */
export async function renderReportsPage(opts: ReportsPageOptions): Promise<void> {
  const { client, company, root } = opts;
  // Owner decision 10: the role the shell holds; an unknown role reads as the
  // account manager, never the owner.
  const role = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  const clockMonth = defaultPeriod();
  clear(root);

  let period = clockMonth;
  // The shape of the period (rd15 R1): one month, the months of a quarter, year
  // to date or custom dates, or the saved window for last year.
  let range: ReportRange = "month";
  let choice: PeriodChoice = "this-month";
  /** The comparison column, when the person asked for one. */
  let compare: CompareChoice = "none";
  /**
   * The comparison each statement was last left with (rd15 R1 round 3). A
   * comparison is a read of its own months, so carrying it onto a statement
   * nobody asked it of spends the minute's requests on a number the person did
   * not choose — a year to date compared is two dozen reads, and the Worker's
   * admin route allows sixty a minute for the whole session. It belongs to the
   * tab it was chosen on: opening another statement starts without one, and
   * coming back to this one restores it for nothing (its months are remembered).
   */
  const compareByReport = new Map<ReportId, CompareChoice>();
  /** A custom range's own dates and months, kept while it is on screen. */
  let custom: { start: string; end: string; months: string[] } | null = null;
  let activeReport: ReportId = "pnl";
  let charts: Chart[] = [];
  let loadToken = 0;
  let loadedAt = new Date();
  let catalog: ReportWindowCatalogItem[] = [];
  /** True once the company's saved-window list has been read for this visit. */
  let catalogRead = false;
  let lastDownload: { filename: string; blob: Blob } | null = null;
  let pyAvailable = false;

  /** This visit's memory of the months it asked about. See MONTH_CACHE_LIMIT. */
  const monthCache = new Map<string, MonthRead | MonthMiss>();
  /**
   * This visit's memory of the books' own answer for a period: which month the
   * books start in, whether the ledger has a loader error, and each account's
   * opening balance. Every statement asks the same question of it, so it is
   * read once for the visit rather than once per tab. A failed read is not kept
   * (see `readContext`).
   */
  const contextCache = new Map<string, BooksContext>();
  /** The write count the memory was built at; a later write empties it. */
  let memoryWrites = client.writes;

  root.append(h("h1", { class: "page-title" }, ["Reports"]));

  const summary = h("p", {
    class: "report-summary",
    "data-role": "report-as-of",
  });
  root.append(summary);

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);

  const tabButtons = new Map<ReportId, HTMLButtonElement>();
  const tabs = h(
    "div",
    { class: "report-tabs", role: "tablist", "aria-label": "Financial reports" },
    REPORT_IDS.map((id) => {
      const btn = h(
        "button",
        {
          type: "button",
          class: "nav-btn",
          role: "tab",
          "aria-selected": id === activeReport ? "true" : "false",
          "data-report": id,
          "aria-label": `Show ${REPORT_LABELS[id]} report`,
        },
        [REPORT_LABELS[id]],
      );
      if (id === activeReport) btn.classList.add("active");
      btn.addEventListener("click", () => {
        // Every statement reads in every period now (rd15 R1); the prior-year
        // report is a month-to-month comparison of its own, so it stays a
        // one-month view.
        if (id === "py-comparison" && (!pyAvailable || range !== "month")) return;
        loadInteractive(id, period);
      });
      tabButtons.set(id, btn);
      return btn;
    }),
  );

  const trigger = h(
    "button",
    {
      type: "button",
      class: "btn report-period-trigger",
      "data-role": "report-period-trigger",
      "aria-haspopup": "listbox",
      "aria-expanded": "false",
      "aria-label": "Reporting period",
    },
    ["This month"],
  );
  const menu = h("div", {
    class: "report-period-menu",
    "data-role": "report-period-menu",
    role: "listbox",
    "aria-label": "Reporting period",
    hidden: "true",
  });
  const choiceButtons = new Map<PeriodChoice, HTMLButtonElement>();
  for (const option of periodChoices()) {
    const button = h(
      "button",
      {
        type: "button",
        class: "report-period-option",
        role: "option",
        "aria-selected": "false",
        "data-preset": option.id,
      },
      [option.label],
    );
    button.addEventListener("click", () => {
      closeMenu();
      choosePeriod(option.id);
    });
    choiceButtons.set(option.id, button);
    menu.append(button);
  }
  trigger.addEventListener("click", () => {
    const open = menu.hidden;
    menu.hidden = !open;
    if (open) menu.removeAttribute("hidden");
    else menu.setAttribute("hidden", "true");
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
  });

  const monthHost = h("div", {
    class: "report-specific-month",
    "data-role": "period-control",
    hidden: "true",
  });
  const monthInput = h("input", {
    type: "month",
    class: "period-label-edit period-input",
    value: period,
    "aria-label": "A specific month",
    "data-role": "report-month-input",
  });
  // Hidden compatibility steppers keep the shell's existing month-stepping
  // hook working. They are never shown: the one dropdown is the visible way
  // to pick dates.
  const previousMonth = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "period-prev",
      "aria-label": "Previous month",
      hidden: "true",
    },
    ["‹"],
  );
  const nextMonth = h(
    "button",
    {
      type: "button",
      class: "period-chevron",
      "data-action": "period-next",
      "aria-label": "Next month",
      hidden: "true",
    },
    ["›"],
  );
  previousMonth.addEventListener("click", () => {
    stepMonth(-1);
  });
  nextMonth.addEventListener("click", () => {
    stepMonth(1);
  });
  monthInput.addEventListener("change", () => {
    const next = monthInput.value;
    if (!isValidPeriod(next)) return;
    choice = "specific-month";
    period = next;
    range = "month";
    paintMenu();
    loadInteractive(activeReport, period);
  });
  monthHost.append(monthInput, previousMonth, nextMonth);

  const rangeForm = h("form", {
    class: "report-range report-custom-dates",
    "data-role": "report-range-form",
    hidden: "true",
  });
  const rangeStart = h("input", {
    type: "date",
    name: "start",
    class: "input",
    "aria-label": "Start date",
    "data-role": "report-range-start",
  });
  const rangeEnd = h("input", {
    type: "date",
    name: "end",
    class: "input",
    "aria-label": "End date",
    "data-role": "report-range-end",
  });
  const rangeSubmit = h(
    "button",
    { type: "submit", class: "btn", "data-role": "report-range-submit" },
    ["Show"],
  );
  rangeForm.append(
    h("label", { class: "report-date-label" }, [h("span", {}, ["From"]), rangeStart]),
    h("label", { class: "report-date-label" }, [h("span", {}, ["To"]), rangeEnd]),
    rangeSubmit,
  );
  rangeForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const start = rangeStart.value.trim();
    const end = rangeEnd.value.trim();
    if (!isIsoDate(start) || !isIsoDate(end)) {
      showOwnSentence("Pick a start date and an end date.");
      return;
    }
    if (start > end) {
      showOwnSentence("The start date is after the end date.");
      return;
    }
    const months = customRangeMonths(start, end, clockMonth);
    if (months.ok) {
      // Whole months: composed here from the monthly statements, so the range
      // comes back now for an owner and an account manager alike (rd15 R1).
      choice = "custom-dates";
      range = "custom";
      custom = { start, end, months: months.months };
      paintMenu();
      loadInteractive(activeReport, period);
      return;
    }
    if (months.why === "too-many") {
      showOwnSentence("Pick a shorter range: up to two years at a time.");
      return;
    }
    if (months.why === "backwards") {
      showOwnSentence("The start date is after the end date.");
      return;
    }
    if (months.why === "future") {
      // Months after this one have no books yet. Reading them would list them
      // as "not available yet", which blames the books for a date that has not
      // arrived.
      showOwnSentence("Pick a range that ends this month or earlier.");
      return;
    }
    // Not whole months, so the monthly statements cannot compose it. Only the
    // owner may ask the Worker for the exact window, and that one is built at
    // the next month-end close.
    if (!can(role, "request_report_window")) {
      // The account manager may read any range of whole months; the exact-date
      // window is the owner's, so the shared capability sentence names who to
      // ask rather than leaving them with a refusal.
      showOwnSentence(
        "Pick whole months to see a range now: the first day of one month to the last day of a later month. " +
          advice("request_report_window", role),
      );
      return;
    }
    choice = "custom-dates";
    paintMenu();
    showSummary({ start, end, asOf: false }, "Not closed or reconciled");
    void requestWindow(`${start}:${end}`);
  });

  const compareSelect = h(
    "select",
    {
      class: "input report-compare-select",
      name: "compare",
      "data-role": "report-compare",
      "aria-label": "Compare to",
    },
    [
      h("option", { value: "none" }, ["No comparison"]),
      h("option", { value: "previous" }, ["The period before"]),
      h("option", { value: "same-last-year" }, ["The same period last year"]),
    ],
  );
  compareSelect.addEventListener("change", () => {
    compare = compareChoiceOf(compareSelect.value);
    // The choice is this tab's own, remembered for when the person comes back
    // to it; another statement keeps whatever it was left with.
    compareByReport.set(activeReport, compare);
    paintMenu();
    loadInteractive(activeReport, period);
  });
  const compareHost = h(
    "div",
    { class: "report-range report-compare", "data-role": "report-compare-host", hidden: "true" },
    [h("label", { class: "report-date-label" }, [h("span", {}, ["Compare to"]), compareSelect])],
  );

  const periodBarChildren: HTMLElement[] = [
    h("div", { class: "report-period-picker" }, [trigger, menu]),
    monthHost,
    rangeForm,
    compareHost,
  ];
  const periodBar = h("div", { class: "report-period-bar", "data-role": "report-presets" }, periodBarChildren);
  root.append(h("div", { class: "toolbar" }, [tabs, periodBar]));

  const downloadBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-role": "report-download",
      hidden: "true",
    },
    ["Download CSV"],
  );
  downloadBtn.addEventListener("click", () => {
    if (lastDownload === null) return;
    saveDownload(lastDownload.filename, lastDownload.blob);
  });

  const paused = h("p", {
    class: "report-paused",
    "data-role": "report-paused",
    hidden: "true",
  });
  root.append(paused);

  const band = h("div", { class: "report-band", "data-role": "report-band", hidden: "true" });
  root.append(band);

  const missingHost = h("div", { "data-role": "report-missing", hidden: "true" });
  root.append(missingHost);

  // The comparison column's own trouble, said once: a column that could not be
  // read must not print as a row of zeroes.
  const compareMissingHost = h("div", {
    "data-role": "report-compare-missing",
    hidden: "true",
  });
  root.append(compareMissingHost);

  const banner = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "reports-error",
    hidden: "true",
  }, []);
  root.append(banner);

  const statusHost = h("div", { "data-role": "reports-status" });
  root.append(statusHost);

  const canvas = h("div", {
    role: "region",
    "aria-label": landmarkName(activeReport, period),
    "data-role": "report-canvas",
  });
  root.append(canvas);
  root.append(downloadBtn);

  function periodChoices(): ReadonlyArray<{ id: PeriodChoice; label: string }> {
    // Every reader gets every period (rd15 R1): the account manager reads the
    // books, so a range is theirs too. Custom dates compose live when they are
    // whole months; only the exact-date window request is the owner's, and the
    // page says so at the moment it is needed (`can(role, ...)`).
    return [
      { id: "this-month", label: "This month" },
      { id: "last-month", label: "Last month" },
      { id: "this-quarter", label: "This quarter" },
      { id: "this-year", label: "Year to date" },
      { id: "last-year", label: "Last year" },
      { id: "specific-month", label: "A specific month" },
      { id: "custom-dates", label: "Custom dates" },
    ];
  }

  function closeMenu(): void {
    menu.hidden = true;
    menu.setAttribute("hidden", "true");
    trigger.setAttribute("aria-expanded", "false");
  }

  function paintFreshness(): void {
    clear(freshnessHost);
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: loadedAt,
        onRefresh: () => {
          // Refresh means "ask the server again": the page's own memory of the
          // months, the books' context and the saved-window list all go, and so
          // does the client's 30 s read cache, or the press would repaint the
          // same answers.
          invalidateReads();
          client.clearReadCache();
          loadInteractive(activeReport, period);
        },
      }),
    );
  }

  /**
   * The page's own memory of what it read, emptied for the whole of the next
   * load. Everything here is remembered for ONE visit and under one rule: it is
   * only good while the books under it hold still, so a write this client sent
   * empties it too (see `forgetStaleReads`).
   */
  function invalidateReads(): void {
    monthCache.clear();
    contextCache.clear();
    catalog = [];
    catalogRead = false;
    memoryWrites = client.writes;
  }

  /**
   * A write landed while this visit was open (a booking approved in another
   * surface, this page's own report-window request): the books under the
   * memory have changed, so nothing in it is worth serving.
   */
  function forgetStaleReads(): void {
    if (client.writes === memoryWrites) return;
    invalidateReads();
  }

  /** The last coverage the strip printed, so a status change keeps it. */
  let shownCoverage: Coverage | null = null;

  function showSummary(coverage: Coverage, status: ReportStatusKey): void {
    shownCoverage = coverage;
    clear(summary);
    summary.append(
      h("span", { "data-role": "report-period-coverage" }, [coverageText(coverage, activeReport)]),
      " · ",
      h("span", { "data-role": "report-band-status", "data-status": status }, [status]),
    );
  }

  /**
   * Change only the strip's status, keeping the coverage it already names: a
   * saved custom range must not start claiming the dropdown's month.
   */
  function showStatus(status: ReportStatusKey): void {
    showSummary(
      shownCoverage ?? coverageForPeriod(period, activeReport, clockMonth),
      status,
    );
  }

  function paintMenu(): void {
    const selected = periodChoices().find((option) => option.id === choice);
    trigger.textContent = selected?.label ?? "This month";
    for (const [id, button] of choiceButtons) {
      const on = id === choice;
      button.setAttribute("aria-selected", on ? "true" : "false");
      button.classList.toggle("active", on);
    }
    const showMonth = choice === "specific-month";
    monthHost.hidden = !showMonth;
    if (showMonth) monthHost.removeAttribute("hidden");
    else monthHost.setAttribute("hidden", "true");
    const showCustom = choice === "custom-dates";
    rangeForm.hidden = !showCustom;
    if (showCustom) rangeForm.removeAttribute("hidden");
    else rangeForm.setAttribute("hidden", "true");
    monthInput.value = period;
    for (const [id, button] of tabButtons) {
      // Only the prior-year report is limited to one month: it compares a month
      // with the same month a year earlier, in the payload's own columns.
      const reportAllowed = id !== "py-comparison" || range === "month";
      const pyAllowed = id !== "py-comparison" || pyAvailable;
      const show = reportAllowed && pyAllowed;
      button.hidden = !show;
      if (show) button.removeAttribute("hidden");
      else button.setAttribute("hidden", "true");
    }
    paintCompare();
  }

  /** The comparison column exists only where one money column has a home. */
  function compareAllowed(): boolean {
    // A saved last-year window is one period already; By Location has three
    // money columns; the prior-year report is a comparison in its own columns.
    if (choice === "last-year") return false;
    return (
      activeReport === "pnl" ||
      activeReport === "balance-sheet" ||
      activeReport === "cash-flow"
    );
  }

  function paintCompare(): void {
    const show = compareAllowed();
    if (show) {
      // The choice belongs to the tab it was chosen on: coming back to this
      // statement restores it, and a range that cannot carry a column does not
      // count as the person turning it off (see below).
      compare = compareByReport.get(activeReport) ?? "none";
    } else if (compare !== "none") {
      // No control, no column: a choice nobody can see or change must not keep
      // printing a comparison. It is kept for every tab rather than dropped, and
      // for this one too, because picking a saved last-year window is not "the
      // person turned it off" for the next range they read — and a per-tab
      // delete would be a different rule for each tab of the same page.
      compareByReport.set(activeReport, compare);
      compare = "none";
    }
    compareSelect.value = compare;
    compareHost.hidden = !show;
    if (show) compareHost.removeAttribute("hidden");
    else compareHost.setAttribute("hidden", "true");
  }

  function stepMonth(delta: number): void {
    const next = shiftPeriod(period, delta);
    if (!isValidPeriod(next)) return;
    choice = "specific-month";
    period = next;
    range = "month";
    monthInput.value = period;
    paintMenu();
    loadInteractive(activeReport, period);
  }

  function choosePeriod(next: PeriodChoice): void {
    choice = next;
    paintMenu();
    if (next === "this-month" || next === "last-month") {
      range = "month";
      period = next === "this-month" ? clockMonth : shiftPeriod(clockMonth, -1);
      monthInput.value = period;
      loadInteractive(activeReport, period);
      return;
    }
    if (next === "this-quarter" || next === "this-year") {
      range = next === "this-quarter" ? "quarter" : "year";
      custom = null;
      // The report that was open stays open (rd15 R1): every statement reads
      // over a range, so a quarter of Cash Flow is one press away.
      loadInteractive(activeReport, clockMonth);
      return;
    }
    if (next === "last-year") {
      range = "last-year";
      custom = null;
      // The saved last-year window of whichever report is open.
      loadInteractive(activeReport, clockMonth);
      return;
    }
    if (next === "specific-month") {
      const coverage = coverageForPeriod(period, activeReport, clockMonth);
      showSummary(coverage, "Loading…");
      monthInput.focus();
      return;
    }
    const coverage = coverageForCustom(rangeStart.value, rangeEnd.value, period);
    showSummary(coverage, "Loading…");
    rangeStart.focus();
  }

  function setActiveTab(report: ReportId): void {
    for (const [id, btn] of tabButtons) {
      const active = id === report;
      btn.classList.toggle("active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
    }
  }

  function hideBanner(): void {
    clear(banner);
    banner.hidden = true;
    banner.setAttribute("hidden", "true");
  }

  function hideStatus(): void {
    clear(statusHost);
  }

  function clearPaper(): void {
    destroyCharts(charts);
    charts = [];
    clear(canvas);
    canvas.classList.remove("report-canvas");
  }

  function showOwnSentence(text: string): void {
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    banner.append(h("p", {}, [text]));
  }

  function showLoading(): void {
    clearPaper();
    clearCompareNote();
    hideBanner();
    clear(band);
    band.hidden = true;
    band.setAttribute("hidden", "true");
    clear(missingHost);
    missingHost.hidden = true;
    missingHost.setAttribute("hidden", "true");
    hideStatus();
    statusHost.append(h("p", { class: "muted", "data-role": "reports-loading" }, ["Loading report…"]));
  }

  function retryButton(): HTMLButtonElement {
    const button = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry", "aria-label": "Try again" },
      ["Try again"],
    );
    button.addEventListener("click", () => {
      loadInteractive(activeReport, period);
    });
    return button;
  }

  function showMessage(role: string, text: string, links: HTMLElement | null): void {
    clearPaper();
    hideStatus();
    const block = h("div", { "data-role": role });
    block.append(h("p", {}, [text]));
    if (links !== null) block.append(links);
    statusHost.append(block);
  }

  /**
   * "Connection status" for the owner's next step on a month the ledger could
   * not build (rd Z4 C-12). The account manager's sentence names the owner
   * instead, so the control is drawn only where the sentence points at it.
   */
  function connectionStatusButton(): HTMLButtonElement {
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "open-connection-status",
        "aria-label": navLabel("health"),
      },
      [navLabel("health")],
    );
    button.addEventListener("click", () => {
      navigateTo("health");
    });
    return button;
  }

  function activityLinks(forPeriod: string): HTMLElement {
    const see = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "see-activity" },
      [`See ${navLabel("transactions")}`],
    );
    see.addEventListener("click", () => {
      navigateTo("transactions", { period: forPeriod });
    });
    // A month whose numbers are missing can have cards waiting, but this page
    // makes no queue read (its four-read budget is at the ceiling) and the
    // role-free header count is not a number anyone may print (item B4b-1). So
    // the way there is the page's own name, with no count and no claim.
    const answer = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "answer-waiting" },
      [navLabel("queue")],
    );
    answer.addEventListener("click", () => {
      navigateTo("queue");
    });
    return h("p", { class: "report-links" }, [see, retryButton(), answer]);
  }

  function paintProblem(
    problem: ReportProblem,
    report: ReportId,
    forPeriod: string,
    coverageOverride?: Coverage,
  ): void {
    clearPaper();
    hideStatus();
    clear(band);
    band.hidden = true;
    band.setAttribute("hidden", "true");
    const coverage = coverageOverride ?? coverageForPeriod(forPeriod, report, clockMonth);
    const copy = problemCopy(problem, report, forPeriod, clockMonth, role);
    if (copy.activity) {
      hideBanner();
      // The strip describes the numbers, never the bookings (rd Y3 MUST-FIX):
      // a month nobody has loaded says the numbers are missing, not that
      // nothing is booked. rd Z4 B9-H1.
      showSummary(coverage, "Numbers are unavailable right now");
      showMessage("reports-empty", copy.text, activityLinks(forPeriod));
      return;
    }
    if (copy.unavailable) {
      hideBanner();
      // The numbers are missing, not the bookings: cards can be waiting while
      // the saved books copy is off or a statement failed to build. The strip
      // stays neutral and the sentence says which thing is missing.
      showSummary(coverage, "Numbers are unavailable right now");
      const actions = copy.retry || copy.connectionStatus ? h("p", { class: "report-links" }) : null;
      if (actions !== null && copy.retry) actions.append(retryButton());
      if (actions !== null && copy.connectionStatus) actions.append(connectionStatusButton());
      showMessage("reports-unavailable", copy.text, actions);
      return;
    }
    showSummary(coverage, "Numbers are unavailable right now");
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    banner.append(h("p", {}, [copy.text]));
    if (copy.retry) banner.append(retryButton());
  }

  /**
   * The ledger's own error state, when it was read: `null` means the header
   * could not be read at all, so this page does not know. Painting the
   * all-clear for an unknown would hide a real error behind a failed request,
   * so the unknown says what it is.
   */
  function paintPause(loaderErrors: number | null): void {
    if (loaderErrors === null) {
      paused.hidden = false;
      paused.removeAttribute("hidden");
      paused.textContent =
        "The books could not be read just now, so this page cannot say whether the ledger has an error.";
      return;
    }
    if (loaderErrors > 0) {
      paused.hidden = false;
      paused.removeAttribute("hidden");
      paused.textContent = "Numbers are paused because the ledger has an error.";
      return;
    }
    paused.hidden = true;
    paused.setAttribute("hidden", "true");
    paused.textContent = "";
  }

  function clearCompareNote(): void {
    clear(compareMissingHost);
    compareMissingHost.hidden = true;
    compareMissingHost.setAttribute("hidden", "true");
  }

  /**
   * What the comparison column could not read, and what the period on screen
   * could not read when a Difference is printed between them. None of the
   * comparison: one sentence saying there is nothing to compare with, so no
   * column is drawn. Some of it: the column is drawn short and the sentence
   * says so.
   *
   * A Difference is a subtraction, so BOTH sides must be the period they claim.
   * When a month of the period on screen is missing or failed, the Difference
   * column is not drawn either, and this names the month that is not there.
   */
  function paintCompareNote(
    read: CompareRead,
    scope?: { missing: readonly string[]; failed: readonly { period: string }[] },
  ): void {
    clear(compareMissingHost);
    const sentences: string[] = [];
    if (read.nothingAt !== null) {
      sentences.push(
        `Nothing to compare with: ${formatPeriodLabel(read.nothingAt)} is not available yet.`,
      );
    } else if (read.partial) {
      sentences.push(
        "Some months in the comparison are not available yet, so the difference is not shown.",
      );
    }
    if (read.report !== undefined && read.report.difference === false && scope !== undefined) {
      const short = differenceNotShownSentence(scope);
      if (short !== "") sentences.push(short);
    }
    if (sentences.length === 0) {
      compareMissingHost.hidden = true;
      compareMissingHost.setAttribute("hidden", "true");
      return;
    }
    compareMissingHost.hidden = false;
    compareMissingHost.removeAttribute("hidden");
    for (const text of sentences) {
      compareMissingHost.append(h("p", { class: "muted" }, [text]));
    }
  }

  function paintBand(lines: readonly string[]): void {
    clear(band);
    if (lines.length === 0) {
      band.hidden = true;
      band.setAttribute("hidden", "true");
      return;
    }
    band.hidden = false;
    band.removeAttribute("hidden");
    for (const line of lines) {
      band.append(h("p", { "data-role": "report-provisional" }, [line]));
    }
  }

  function paintMissing(
    current: { missing: readonly string[]; failed: readonly { period: string }[] },
    comparison: { missing: readonly string[]; failed: readonly { period: string }[] },
  ): void {
    clear(missingHost);
    const empty =
      current.missing.length === 0 &&
      current.failed.length === 0 &&
      comparison.missing.length === 0 &&
      comparison.failed.length === 0;
    if (empty) {
      missingHost.hidden = true;
      missingHost.setAttribute("hidden", "true");
      return;
    }
    missingHost.hidden = false;
    missingHost.removeAttribute("hidden");
    missingHost.append(
      h("ul", {}, [
        ...current.missing.map((item) => h("li", {}, [missingMonthSentence(item)])),
        ...current.failed.map((item) => h("li", {}, [failedMonthSentence(item.period)])),
        // The comparison's own months are said in their own words: a reader
        // looking at the list must be able to tell which period each month
        // belongs to, and a range can hold the same month name twice.
        ...comparison.missing.map((item) =>
          h("li", {}, [missingComparisonMonthSentence(item)]),
        ),
        ...comparison.failed.map((item) =>
          h("li", {}, [failedComparisonMonthSentence(item.period)]),
        ),
      ]),
    );
  }

  function setDownload(filename: string, body: string | Uint8Array, type: string): void {
    lastDownload = { filename, blob: downloadBlob(body, type) };
    downloadBtn.hidden = false;
    downloadBtn.removeAttribute("hidden");
    if (type.includes("pdf")) {
      downloadBtn.textContent = "Download PDF";
      downloadBtn.setAttribute("aria-label", "Download PDF of this statement");
    } else if (type.includes("csv")) {
      downloadBtn.textContent = "Download CSV";
      downloadBtn.setAttribute("aria-label", "Download CSV");
    } else {
      downloadBtn.textContent = "Download";
      downloadBtn.setAttribute("aria-label", "Download");
    }
  }

  function setPdfDownload(source: ReportPdfInput): void {
    const pdf = buildReportPdf(source);
    setDownload(pdf.filename, pdf.bytes, "application/pdf");
  }

  function setCsvDownload(table: ShownTable, filename: string): void {
    setDownload(filename, tableCsv(table), "text/csv");
  }

  function clearDownload(): void {
    lastDownload = null;
    downloadBtn.hidden = true;
    downloadBtn.setAttribute("hidden", "true");
  }

  function usePaper(report: ReportId, forPeriod: string): void {
    canvas.classList.add("report-canvas");
    canvas.setAttribute("aria-label", landmarkName(report, forPeriod));
  }

  /**
   * The saved windows for the WHOLE company, read once for the visit (rd15 R1
   * round 3). The list is the company's, not the statement's — every row names
   * its own report and the picker filters to the one on screen — so asking
   * again for each tab spends a request per tab on the same answer.
   */
  async function refreshCatalog(): Promise<void> {
    if (catalogRead) return;
    forgetStaleReads();
    try {
      const result = await client.listReportWindows(company);
      catalog = result.items;
      catalogRead = true;
    } catch {
      // A failed read is not remembered: the next load asks again.
      catalog = [];
    }
  }

  async function refreshPyAvailability(): Promise<void> {
    let cache = pyAvailabilityByClient.get(client);
    if (cache === undefined) {
      cache = new Map<string, boolean>();
      pyAvailabilityByClient.set(client, cache);
    }
    const cached = cache.get(company);
    if (cached !== undefined) {
      pyAvailable = cached;
    } else {
      try {
        const snapshot = await client.getReport(company, "py-comparison", clockMonth);
        const row = snapshot.data;
        pyAvailable = row.available && row.lines.length > 0;
        cache.set(company, pyAvailable);
      } catch (error) {
        pyAvailable = false;
        if (error instanceof ConsoleDataError && error.status === 404) {
          cache.set(company, false);
        }
      }
    }
    if (!pyAvailable && activeReport === "py-comparison") {
      activeReport = "pnl";
      setActiveTab("pnl");
    }
    paintMenu();
  }

  async function loadLastYear(): Promise<void> {
    const token = ++loadToken;
    paintMenu();
    const coverage = lastYearCoverage(clockMonth);
    hideBanner();
    clearDownload();
    showLoading();
    showSummary(coverage, "Loading…");
    await refreshCatalog();
    if (token !== loadToken || !stillCurrent()) return;
    const item = catalog.find(
      (row) => row.typicalSlot === "last_year" && row.report === activeReport,
    );
    if (item === undefined) {
      showSummary(coverage, "No saved report");
      showMessage("reports-empty", "There is no saved report for last year yet.", null);
      return;
    }
    if (item.status === "pending") {
      // One wording for this state on both paths (rd Y3): the sentence already
      // says it is only saved, so the strip must not say "No saved report".
      showStatus("Saved for the next close");
      showMessage(
        "reports-pending",
        "Saved. It will be produced at the next month-end close.",
        null,
      );
      return;
    }
    if (item.status === "failed") {
      showSummary(coverage, "Numbers are unavailable right now");
      showMessage("reports-failed", "Could not load this report window. Try again.", retryButton());
      return;
    }
    await loadWindow(item.id, activeReport, coverage);
  }

  async function loadWindow(
    id: string,
    report: ReportId,
    coverageHint?: Coverage,
  ): Promise<void> {
    const token = ++loadToken;
    hideBanner();
    showLoading();
    try {
      const downloaded = await client.getReportWindow(company, id);
      if (token !== loadToken || !stillCurrent()) return;
      loadedAt = new Date();
      paintFreshness();
      hideStatus();
      if (downloaded.json === undefined) {
        setDownload(downloaded.filename, downloaded.text, downloaded.contentType);
        showSummary(coverageHint ?? coverageForPeriod(period, report, clockMonth), "Closed");
        showMessage("reports-csv", "CSV ready — use Download CSV.", null);
        return;
      }
      const envelope = downloaded.json as AdhocWindowEnvelope<ReportDataMap[typeof report]>;
      const snapshot = snapshotFromWindow(envelope, report);
      const coverage = coverageForWindow(envelope, period, report);
        setPdfDownload(pdfInputFromEnvelope(envelope, report, companies));
      if (reportIsEmpty(report, snapshot.data)) {
        const empty = emptyPeriodCopy(report, coverage, clockMonth);
        showSummary(coverage, empty.status);
        showMessage("reports-empty", empty.sentence, null);
        return;
      }
      clearPaper();
      usePaper(report, envelope.window);
      charts = renderReport(canvas, report, snapshot, { openMonth: false, companies });
      paintBand([]);
      showSummary(coverage, "Closed");
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      clearPaper();
      clearDownload();
      paintProblem(problemFrom(error), report, period);
    }
  }

  /** The "Try again" control for a window request: the same window, not a new one. */
  function windowTryAgain(windowToken: string): HTMLButtonElement {
    const again = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry", "aria-label": "Try again" },
      ["Try again"],
    );
    again.addEventListener("click", () => {
      void requestWindow(windowToken);
    });
    return again;
  }

  /** What a landed window request meant, in the page's own words. */
  async function paintWindowRequest(
    result: Awaited<ReturnType<AdminClient["requestReportWindow"]>>,
    windowToken: string,
  ): Promise<void> {
    // A landed request means new statements exist: anything this session
    // remembered about the months is now worth re-reading.
    invalidateReads();
    await refreshCatalog();
    if (!stillCurrent()) return;
    if (result.status === "pending") {
      hideBanner();
      clearDownload();
      // The strip says the same thing as the sentence (rd Y3): a saved window
      // is not "not closed or reconciled", which is what the submit set.
      showStatus("Saved for the next close");
      showMessage(
        "reports-pending",
        "Saved. It will be produced at the next month-end close.",
        null,
      );
      return;
    }
    if (result.status === "failed") {
      hideBanner();
      clearDownload();
      showStatus("Numbers are unavailable right now");
      // Try again repeats what the person asked for: the same window, not the
      // month in the dropdown.
      showMessage(
        "reports-failed",
        // The request landed; the server's build of the window did not run.
        // Never "could not save": the save is exactly what did happen.
        "The report window could not be built. Try again.",
        windowTryAgain(windowToken),
      );
      return;
    }
    // ready: the window exists, so show it instead of leaving the page on the
    // loader or on the previous statement.
    await loadWindow(result.id, activeReport);
  }

  /**
   * Ask for a report window (item B4b-7). One press sends once: `runWrite`
   * owns the flight, the disabled controls and the re-read, so a held key or a
   * second click cannot ask twice. A landed request is worded by the page
   * (`paintWindowRequest`); a failure is worded by the shared outcome table, so
   * a 5xx or a lost answer reads "we could not confirm", never "did not save".
   */
  async function requestWindow(windowToken: string): Promise<void> {
    const token = ++loadToken;
    const report = activeReport;
    const outcome = await runWrite({
      key: "report-window:" + report + ":" + windowToken,
      scope: periodBar,
      gate: () => null,
      send: () => client.requestReportWindow(company, { report, windowToken }),
      afterLanded: (result) => paintWindowRequest(result, windowToken),
      stillCurrent: () => token === loadToken && stillCurrent(),
    });
    if (outcome.kind === "landed") return;
    // A gate refusal sent nothing and already said why; there is no gate here,
    // so this is only the defensive path.
    if (outcome.kind === "refused" && outcome.status === 0) return;
    if (!stillCurrent()) return;
    showWindowFailure(outcome, windowToken);
  }

  /** The shared write-outcome words, with the page's own retry beside them. */
  function showWindowFailure(
    outcome: Exclude<WriteOutcome, { kind: "landed" }>,
    windowToken: string,
  ): void {
    hideBanner();
    clearDownload();
    showStatus("Numbers are unavailable right now");
    const copy =
      outcome.error !== undefined
        ? failureCopy(outcome.error, { role, context: "write" })
        : { sentence: outcomeSentence(outcome, role), detail: null };
    showMessage("reports-failed", copy.sentence, windowTryAgain(windowToken));
  }

  /** The token the canvas, the CSV file name and the printed page are named from. */
  /**
   * The months the statement on screen was composed from (rd15 A10). A drilled
   * figure opens this span; an empty list means the page is reading one month
   * the payload itself names.
   */
  let currentMonths: string[] = [];

  /** First day to last day of the months on screen, when there is more than one. */
  function drillRange(): { from: string; to: string } | null {
    const first = currentMonths[0];
    const last = currentMonths[currentMonths.length - 1];
    if (first === undefined || last === undefined) return null;
    if (currentMonths.length < 2) return null;
    const end = lastDayOfMonth(last);
    if (end === null) return null;
    return { from: `${first}-01`, to: end };
  }

  /** `2026-02` -> `2026-02-28`; null when the month is not a month. */
  function lastDayOfMonth(month: string): string | null {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
    if (match === null) return null;
    const year = Number(match[1]);
    const monthNumber = Number(match[2]);
    const day = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
    const yearText = match[1] ?? "";
    const monthText = match[2] ?? "";
    return `${yearText}-${monthText}-${String(day).padStart(2, "0")}`;
  }

  function rangeSpanToken(forPeriod: string): string {
    if (range === "month") return forPeriod;
    if (range === "custom" && custom !== null) {
      const first = custom.months[0] ?? "";
      const last = custom.months[custom.months.length - 1] ?? "";
      return first !== "" && last !== "" ? `${first}:${last}` : range;
    }
    if (range === "last-year") return "last year";
    return range;
  }

  async function load(report: ReportId, forPeriod: string): Promise<void> {
    // The prior-year report is a month beside the same month a year earlier, in
    // the payload's own two columns: it has no range form. Picking a range
    // while it is open lands on Profit & Loss, which does — rather than on
    // "this statement could not be built" with the tab hidden.
    if (report === "py-comparison" && range !== "month" && range !== "last-year") {
      activeReport = "pnl";
      setActiveTab("pnl");
      paintMenu();
      await load("pnl", forPeriod);
      return;
    }
    if (report !== activeReport) {
      // A comparison belongs to the tab it was chosen on: opening another
      // statement starts without one (see `compareByReport`).
      compare = compareByReport.get(report) ?? "none";
    }
    activeReport = report;
    setActiveTab(report);
    paintMenu();
    if (range === "last-year") {
      // The saved window is produced by the pipeline, so it says its own
      // period: nothing here composes or names it in advance.
      await loadLastYear();
      return;
    }
    const span = rangeSpanToken(forPeriod);
    canvas.setAttribute("aria-label", landmarkName(report, span));
    const token = ++loadToken;
    hideBanner();
    showLoading();
    clearDownload();
    const coverage =
      range === "month"
        ? coverageForPeriod(forPeriod, report, clockMonth)
        : coverageForScope(range, clockMonth, report, custom);
    showSummary(coverage, "Loading…");
    await Promise.all([refreshCatalog(), refreshPyAvailability()]);
    if (token !== loadToken || !stillCurrent()) return;
    const contextPeriod = range === "month" ? forPeriod : clockMonth;
    const books = await readContext(contextPeriod);
    if (token !== loadToken || !stillCurrent()) return;
    // A failed read is not "no error": the line says it does not know rather
    // than claiming the ledger is clean. A month nobody has published has no
    // header to warn about, and the page already names the absent month.
    const loaderErrors =
      books.kind === "read" ? books.context.loaderErrors : books.kind === "failed" ? null : 0;
    paintPause(loaderErrors);
    const firstPublished = books.kind === "read" ? books.context.firstPublished : null;
    try {
      if (range !== "month") {
        await loadRange(token, report, firstPublished);
        return;
      }
      const read = await readMonthCached(report, forPeriod);
      if (token !== loadToken || !stillCurrent()) return;
      if (!read.ok) {
        paintMissing({ missing: [], failed: [] }, { missing: [], failed: [] });
        paintProblem(read.problem, report, forPeriod);
        return;
      }
      const comparison = await readComparison(report, [forPeriod]);
      if (token !== loadToken || !stillCurrent()) return;
      paintMissing(
        { missing: [], failed: [] },
        { missing: comparison.missing, failed: comparison.failed },
      );
      drawStatement(
        report,
        forPeriod,
        read.statement,
        books.kind === "read" ? books.context.closings : new Map<string, string>(),
        true,
        comparison.report,
        [],
        readTime(read.statement, comparison),
      );
      paintCompareNote(comparison);
    } catch (error) {
      if (token !== loadToken || !stillCurrent()) return;
      paintProblem(problemFrom(error), report, forPeriod);
    }
  }

  /** The months this period covers, oldest first, before any point-in-time rule. */
  function periodMonths(firstPublished: string | null): string[] {
    if (range === "custom") return custom?.months ?? [];
    if (range === "quarter" || range === "year") {
      return statementPeriods({ preset: range, now: new Date(), firstPublished });
    }
    return [];
  }

  /**
   * A balance sheet is a point in time, not a span: the period it belongs to
   * is the day it is "as of", so a range reads its last month only.
   */
  function pointInTimeMonths(report: ReportId, months: string[]): string[] {
    return report === "balance-sheet" ? months.slice(-1) : months;
  }

  /**
   * One month's statement, from this visit's own memory when it is there.
   * The key names the company, the report, the month and the month's relation
   * to the clock, so a remembered answer can never be served to a different
   * question. A success is kept, and so is a month nobody has published: that
   * one costs two requests (the saved statement, then the live ledger) and can
   * answer nothing else for the rest of the visit. A failed read is not kept.
   */
  async function readMonthCached(report: ReportId, period: string): Promise<MonthRead | MonthMiss> {
    // A write landed since this visit began (a booking approved in another
    // surface, the page's own report-window request): the books under this
    // memory have changed, so it is not worth serving.
    forgetStaleReads();
    const relation = periodRelation(period, clockMonth);
    const key = `${slugText(company)}\u0000${report}\u0000${period}\u0000${relation}`;
    const hit = monthCache.get(key);
    if (hit !== undefined) return hit;
    // The write count BEFORE the read. A write that lands while the answer is
    // in flight makes the answer the statement from before it, so it is
    // returned to the caller that asked (there is nothing better to show) but
    // never remembered: remembering it would file a pre-write figure under the
    // post-write baseline and serve it as fresh.
    const writesBeforeRead = client.writes;
    const read = await readMonth(client, company, report, period, clockMonth);
    if (client.writes !== writesBeforeRead) {
      invalidateReads();
      return read;
    }
    if (read.ok || isNotPublishedAnswer(read)) {
      if (monthCache.size >= MONTH_CACHE_LIMIT) {
        const oldest = monthCache.keys().next();
        if (!oldest.done) monthCache.delete(oldest.value);
      }
      monthCache.set(key, read);
    }
    return read;
  }

  /**
   * The books' own context for a period, read once for the visit. Every
   * statement asks the same question of it (which month the books start in,
   * whether the ledger has a loader error, each account's opening balance), so
   * a tab switch must not spend a request asking it again.
   *
   * Read once is not read once forever: only an ANSWER is remembered. "Nobody
   * has published a header for this month" is an answer — it can only answer
   * the same way until the month is published, and paying a request per tab for
   * it would spend the minute (the rule the month reads use). A failed read is
   * not an answer: the next load asks again. Remembering one would take a single
   * transient refusal (a 429 is enough) and make it the visit's verdict — every
   * later range would begin at no month, reading and listing the months before
   * the books, and the ledger-error line would read as a clean ledger.
   */
  async function readContext(period: string): Promise<BooksContext> {
    forgetStaleReads();
    const hit = contextCache.get(period);
    if (hit !== undefined) return hit;
    const writesBeforeRead = client.writes;
    const answer = await readLedgerContext(client, company, period);
    if (client.writes !== writesBeforeRead) {
      invalidateReads();
      return answer;
    }
    if (answer.kind !== "failed") contextCache.set(period, answer);
    return answer;
  }

  /**
   * One read per month, with each month's fate counted apart: absent (never
   * published), failed (a real error), or the whole view switched off. One
   * failed month must not throw away the months that did load (rd Y3).
   *
   * At most four months are in flight at once. The Worker's admin route allows
   * 60 requests a minute per IP, and a year to date with a comparison asks for
   * two dozen months: launching them all at once spends the limit in one burst
   * and answers a person with "Too many requests" for the next thing they
   * click. Order is preserved by slot, so Cash Flow still opens on the oldest
   * month and closes on the newest whatever order the answers arrive in.
   */
  async function readScope(report: ReportId, months: readonly string[]): Promise<ScopeRead> {
    const slots: (MonthRead | MonthMiss | undefined)[] = [];
    let cursor = 0;
    const worker = async (): Promise<void> => {
      for (;;) {
        const index = cursor;
        cursor += 1;
        const period = months[index];
        if (period === undefined) return;
        slots[index] = await readMonthCached(report, period);
      }
    };
    const workers = Math.max(1, Math.min(SCOPE_CONCURRENCY, months.length));
    await Promise.all(Array.from({ length: workers }, worker));
    const out: ScopeRead = {
      loaded: [],
      missing: [],
      failed: [],
      viewOff: false,
    };
    for (let index = 0; index < months.length; index += 1) {
      const item = slots[index];
      const period = months[index];
      if (item === undefined || period === undefined) continue;
      if (item.ok) {
        out.loaded.push(item.statement);
        continue;
      }
      const problem = item.problem;
      if (problem.kind === "missing" && problem.reason === "ledger_view_not_enabled") {
        out.viewOff = true;
        continue;
      }
      if (problem.kind === "missing" && problem.reason === "statements_unavailable") {
        out.failed.push({ period, problem });
        continue;
      }
      if (problem.kind === "missing") {
        out.missing.push(period);
        continue;
      }
      out.failed.push({ period, problem });
    }
    return out;
  }

  /** One statement from the months that loaded, in the report's own shape. */
  function composeScope(
    report: ReportId,
    loaded: readonly LoadedStatement[],
  ): ReportDataMap[ReportId] | null {
    const parts = loaded.map((item) => item.data);
    if (report === "pnl") {
      const pnlParts = parts.filter((part): part is PnlData => isPnlData(part));
      return pnlParts.length === 0 ? null : composePnl(pnlParts);
    }
    if (report === "cash-flow") return composeCashFlow(parts as CashFlowData[]);
    if (report === "by-location") return composeByLocation(parts as ByLocationData[]);
    if (report === "balance-sheet") return parts[0] ?? null;
    return null;
  }

  /**
   * The comparison column's own read. The difference printed beside each
   * amount is computed from these months and the ones on screen; it is never
   * asked for again, so it cannot disagree with the columns it sits between.
   *
   * `months` is the range as the person chose it, before the balance sheet's
   * point-in-time rule. That matters here: a balance sheet compares as of ONE
   * day, so only the last of its comparison months is read. Being handed the
   * already-sliced list would make this rule invisible — and would silently
   * keep reading a year of months the day someone deleted the line.
   */
  async function readComparison(
    report: ReportId,
    months: readonly string[],
  ): Promise<CompareRead> {
    if (compare === "none" || months.length === 0) {
      return noComparison();
    }
    const wanted = compareMonths(months, compare, compareBasisOf(range));
    const scoped = report === "balance-sheet" ? wanted.slice(-1) : wanted;
    if (scoped.length === 0) {
      return noComparison();
    }
    const read = await readScope(report, scoped);
    const failed = read.failed;
    const missing = read.missing;
    if (read.loaded.length === 0) {
      return {
        report: undefined,
        missing,
        failed,
        partial: false,
        nothingAt: failed[0]?.period ?? missing[0] ?? scoped[0] ?? null,
        readAt: null,
      };
    }
    const data = composeScope(report, read.loaded);
    if (data === null) {
      return {
        report: undefined,
        missing,
        failed,
        partial: false,
        nothingAt: scoped[0] ?? null,
        readAt: oldestReadAt(read.loaded),
      };
    }
    const partial = read.loaded.length < scoped.length;
    return {
      report: {
        label: compareColumnLabel(scoped),
        note: compareSentence(scoped, report, {
          basis: compareBasisOf(range),
          choice: compare,
          partial,
          missing,
          periodInProgress: periodInProgressOf(range, months, clockMonth),
        }),
        data,
        // A figure summed from some of the period's months is not the period's
        // figure, so it may not be subtracted from a whole one. The column
        // stands, named for what it holds, and the note says which months are
        // missing and that the difference is not shown.
        difference: !partial,
      },
      missing,
      failed,
      partial,
      nothingAt: null,
      readAt: oldestReadAt(read.loaded),
    };
  }

  async function loadRange(
    token: number,
    report: ReportId,
    firstPublished: string | null,
  ): Promise<void> {
    const allMonths = [...periodMonths(firstPublished)];
    let scopeCoverage = coverageForScope(range, clockMonth, report, custom);
    // A custom range may start before the books do. Its early months are not
    // missing, they are not ours, and listing them as "not available yet"
    // would blame the books for a date nobody kept. Begin at the first
    // published month and say so.
    let clampedFrom: string | null = null;
    if (
      range === "custom" &&
      firstPublished !== null &&
      /^\d{4}-(0[1-9]|1[0-2])$/.test(firstPublished) &&
      allMonths.length > 0 &&
      allMonths[0] !== undefined &&
      allMonths[0] < firstPublished
    ) {
      const kept = allMonths.filter((month) => month >= firstPublished);
      if (kept.length === 0) {
        paintMissing({ missing: [], failed: [] }, { missing: [], failed: [] });
        paintBand([]);
        clearDownload();
        const empty = emptyPeriodCopy(report, scopeCoverage, clockMonth);
        showSummary(scopeCoverage, empty.status);
        showMessage(
          "reports-empty",
          `The books start in ${monthText(firstPublished) || firstPublished}. Pick a range from then.`,
          null,
        );
        return;
      }
      clampedFrom = kept[0] ?? null;
      allMonths.splice(0, allMonths.length, ...kept);
      scopeCoverage = {
        start: `${kept[0] ?? ""}-01`,
        end: scopeCoverage.end,
        asOf: scopeCoverage.asOf,
      };
    }
    const notes: string[] = [];
    if (clampedFrom !== null) {
      notes.push(
        `The books start in ${monthText(firstPublished ?? "") || "an earlier month"}, so this range begins there.`,
      );
    }
    const months = pointInTimeMonths(report, allMonths);
    if (months.length === 0) {
      paintMissing({ missing: [], failed: [] }, { missing: [], failed: [] });
      paintBand([]);
      clearDownload();
      const empty = emptyPeriodCopy(report, scopeCoverage, clockMonth);
      showSummary(scopeCoverage, empty.status);
      showMessage("reports-empty", empty.sentence, null);
      return;
    }
    const read = await readScope(report, months);
    if (token !== loadToken || !stillCurrent()) return;
    if (read.viewOff) {
      // The same decided sentence the month path gives, role-aware, with the
      // range's own coverage: a list of months would hide the one cause.
      paintMissing({ missing: [], failed: [] }, { missing: [], failed: [] });
      paintProblem(
        { kind: "missing", reason: "ledger_view_not_enabled" },
        report,
        clockMonth,
        scopeCoverage,
      );
      return;
    }
    const comparison = await readComparison(report, allMonths);
    if (token !== loadToken || !stillCurrent()) return;
    if (
      comparison.report !== undefined &&
      (read.missing.length > 0 || read.failed.length > 0)
    ) {
      // The comparison column may be whole, but the period it would be taken
      // from is not: a Difference between a whole period and a partial one is a
      // wrong number with a right-looking column beside it.
      comparison.report.difference = false;
    }
    paintMissing(
      { missing: read.missing, failed: read.failed },
      { missing: comparison.missing, failed: comparison.failed },
    );
    if (read.loaded.length === 0) {
      paintBand([]);
      clearDownload();
      paintCompareNote(comparison);
      const firstFailure = read.failed[0];
      if (firstFailure !== undefined) {
        // The failed month is not necessarily the clock month: name the one
        // that failed (rd Z4 round-2 MUST-FIX 5b).
        paintProblem(firstFailure.problem, report, firstFailure.period, scopeCoverage);
        return;
      }
      if (read.missing.length > 0) {
        // No month's numbers arrived at all: the strip must not claim there is
        // no income, and the sentence must agree with the list of missing
        // months just above it rather than with the period.
        showSummary(scopeCoverage, "Numbers are unavailable right now");
        showMessage("reports-empty", "None of the months in this period are loaded yet.", null);
        return;
      }
      const empty = emptyPeriodCopy(report, scopeCoverage, clockMonth);
      showSummary(scopeCoverage, empty.status);
      showMessage("reports-empty", empty.sentence, null);
      return;
    }
    const composed = composeScope(report, read.loaded);
    if (composed === null) {
      paintBand([]);
      clearDownload();
      paintCompareNote(comparison);
      paintProblem({ kind: "missing", reason: "statements_unavailable" }, report, clockMonth, scopeCoverage);
      return;
    }
    if (read.missing.length > 0 && reportIsEmpty(report, composed)) {
      // Some months are missing and the ones that loaded have nothing in them:
      // the claim is true only for what arrived, so it says so.
      paintCompareNote(comparison, read);
      showSummary(scopeCoverage, "No income or expenses");
      showMessage("reports-empty", "No income or expenses in the months loaded so far.", null);
      return;
    }
    if (report === "cash-flow") {
      // Closing cash is the last loaded month's own figure and opening cash is
      // the first loaded month's, so a month that did not arrive leaves the
      // three headline numbers short of the period. A failed month is as good
      // as a missing one here, and a month missing at either END moves a
      // headline figure even when the run still foots — so the notes follow the
      // months that did not arrive, not the gap alone.
      const absent = [
        ...read.missing,
        ...read.failed.map((item) => item.period),
      ];
      if (absent.length > 0) {
        if (cashFlowGapCents(composed as CashFlowData) !== 0n) notes.push(CASH_GAP_TEXT);
        const firstMonth = allMonths[0];
        if (firstMonth !== undefined && absent.includes(firstMonth)) {
          notes.push(CASH_FIRST_MONTH_TEXT);
        }
        const lastMonth = allMonths[allMonths.length - 1];
        if (lastMonth !== undefined && absent.includes(lastMonth)) {
          notes.push(CASH_LAST_MONTH_TEXT);
        }
      }
    }
    const open = read.loaded.find((item) => item.state === "open") ?? null;
    const loadedAt = oldestReadAt(read.loaded);
    const statement: LoadedStatement = {
      data: composed,
      state: open !== null || read.missing.includes(clockMonth) ? "open" : "closed",
      throughDate: open?.throughDate ?? null,
      asOf: open?.asOf ?? null,
      generatedAt: open?.generatedAt ?? read.loaded[0]?.generatedAt ?? "",
      readAt: loadedAt ?? Date.now(),
    };
    const span = rangeSpanToken(clockMonth);
    // rd15 A10-A12: the months this page composed, so a drilled figure opens
    // the months behind it, not one of them.
    currentMonths = allMonths;
    const drawn = drawStatement(
      report,
      span,
      statement,
      new Map(),
      true,
      comparison.report,
      notes,
      compareReadTime(loadedAt, comparison),
    );
    paintCompareNote(comparison, read);
    if (drawn) {
      // The person picked a range, so the strip keeps naming it even though the
      // composition's own bounds are the months that happened to load.
      showSummary(
        scopeCoverage,
        statement.state === "closed" ? "Closed" : "Not closed or reconciled",
      );
    }
  }

  function drawStatement(
    report: ReportId,
    forPeriod: string,
    statement: LoadedStatement,
    closings: ReadonlyMap<string, string>,
    drill = true,
    compareData?: ReportCompare,
    notes: readonly string[] = [],
    readAt?: number,
  ): boolean {
    hideStatus();
    hideBanner();
    const data = statement.data;
    const coverage = coverageForStatement(report, data, statement, forPeriod, clockMonth);
    if (!statementReady(report, data)) {
      paintBand([]);
      if (isBlankPayload(data)) {
        const empty = emptyPeriodCopy(report, coverage, clockMonth);
        showSummary(coverage, empty.status);
        showMessage("reports-empty", empty.sentence, null);
        return false;
      }
      paintProblem(
        { kind: "missing", reason: "statements_unavailable" },
        report,
        forPeriod,
      );
      return false;
    }
    if (reportIsEmpty(report, data)) {
      paintBand([]);
      const empty = emptyPeriodCopy(report, coverage, clockMonth);
      showSummary(coverage, empty.status);
      showMessage("reports-empty", empty.sentence, null);
      return false;
    }
    const table = shownTable(report, data, companies, compareData);
    clearPaper();
    usePaper(report, forPeriod);
    const snapshot = toSnapshot(company, report, forPeriod, statement);
    const openMonth = statement.state === "open";
    const renderOptions: {
      openMonth: boolean;
      companies: AccountLabelContext;
      asOfText?: string;
      onAccount?: (account: string) => void;
      onPrefix?: (accountPrefix: string) => void;
      onAccounts?: (accounts: readonly string[]) => void;
      onLocation?: (location: string) => void;
      closings?: ReadonlyMap<string, string>;
      compare?: ReportCompare;
    } = { openMonth, companies };
    if (compareData !== undefined) renderOptions.compare = compareData;
    // rd13 L1: the statement itself names the day it is for, in the same words
    // as the strip above it. One date, from the one coverage read.
    if (report === "balance-sheet" && coverage.end !== "") {
      renderOptions.asOfText = formatShortDate(coverage.end);
    }
    const span = drillRange();
    if (drill && report === "pnl") {
      renderOptions.onAccount = (account: string) => {
        navigateTo(
          "general-ledger",
          span === null ? { account, period: forPeriod } : { account, ...span },
        );
      };
      renderOptions.onPrefix = (accountPrefix: string) => {
        navigateTo(
          "general-ledger",
          span === null ? { accountPrefix, period: forPeriod } : { accountPrefix, ...span },
        );
      };
    }
    if (drill && report === "balance-sheet") {
      renderOptions.onAccount = (account: string) => {
        if (span !== null) {
          // A balance sheet is a date, so a range's sheet is its end month's.
          // The accounts view is that month's, and the line's balance is its.
          const endMonth = currentMonths[currentMonths.length - 1] ?? forPeriod;
          navigateTo("general-ledger", {
            view: "accounts",
            account,
            period: endMonth,
          });
          return;
        }
        navigateTo("general-ledger", { view: "accounts", account, period: forPeriod });
      };
      // A range's sheet IS its end month's figures (composeBalanceSheet takes
      // the last month), so those lines drill to that month's accounts view.
      renderOptions.closings =
        span === null
          ? closings
          : new Map(
              (data as BalanceSheetData).sections.flatMap((section) =>
                section.lines.map((line) => [line.account, line.amount] as const),
              ),
            );
    }
    if (drill && report === "cash-flow") {
      // rd15 A11: the line's own accounts, over the range the figures cover.
      renderOptions.onAccounts = (accounts: readonly string[]) => {
        const filtered = accounts.filter((account) => account !== "");
        if (filtered.length === 0) return;
        navigateTo(
          "general-ledger",
          span === null
            ? { accounts: filtered, period: forPeriod }
            : { accounts: filtered, ...span },
        );
      };
    }
    if (drill && report === "by-location") {
      // rd15 A12: the row's own current name, over the range on screen.
      renderOptions.onLocation = (location: string) => {
        navigateTo(
          "general-ledger",
          span === null ? { location, period: forPeriod } : { location, ...span },
        );
      };
    }
    charts = renderReport(canvas, report, snapshot, renderOptions);
    paintBand([...bandLines(statement), ...notes]);
    showSummary(coverage, statement.state === "closed" ? "Closed" : "Not closed or reconciled");
    // The caption says when the OLDEST number on the page was read, not when
    // the page last repainted: a month served from this visit's memory is as
    // old as the moment it was asked for.
    loadedAt = new Date(readAt ?? Date.now());
    paintFreshness();
    if (table !== null && table.rows.length > 0) {
      // The file names its window the way a person writes it (rd Y3), not the
      // API token: a CSV's only place for metadata that survives a parser is
      // its filename.
      setCsvDownload(
        table,
        `${safeFile(slugText(company))}-${safeFile(report)}-${safeFile(windowText(forPeriod))}.csv`,
      );
    }
    return true;
  }

  function loadInteractive(report: ReportId, forPeriod: string): void {
    void load(report, forPeriod);
  }

  paintMenu();
  await load(activeReport, period);
}

/**
 * The shape of the reporting period (rd15 R1). `month` reads one month;
 * `quarter`, `year` and `custom` compose the months they cover from the same
 * monthly statements; `last-year` shows the saved window the pipeline
 * publishes for last year.
 */
type ReportRange = "month" | "quarter" | "year" | "custom" | "last-year";

type PeriodChoice =
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "this-year"
  | "last-year"
  | "specific-month"
  | "custom-dates";

type ReportStatusKey =
  | "Not closed or reconciled"
  | "Closed"
  | "No income or expenses"
  | "Nothing booked yet"
  | "No saved report"
  | "Saved for the next close"
  | "Loading…"
  | "Numbers are unavailable right now";

interface Coverage {
  start: string;
  end: string;
  asOf: boolean;
}

/** One scope's reads, each month's fate counted apart. */
interface ScopeRead {
  loaded: LoadedStatement[];
  /** Months that are not published (or not published yet). */
  missing: string[];
  /** Months whose read failed, with the problem to word. */
  failed: { period: string; problem: ReportProblem }[];
  /** The Books view is off for the whole company: one cause, not a list. */
  viewOff: boolean;
}

/** What the comparison column could and could not read. */
interface CompareRead {
  /** The column's own period, or undefined when it is not drawn. */
  report: ReportCompare | undefined;
  missing: string[];
  failed: { period: string; problem: ReportProblem }[];
  /** Some of the comparison months did not load, so the column is short. */
  partial: boolean;
  /** The first month the comparison could not read, when none of it loaded. */
  nothingAt: string | null;
  /** When the oldest month behind the column was read; null when it holds none. */
  readAt: number | null;
}

/** No comparison column: the one shape every "not drawn" path returns. */
function noComparison(): CompareRead {
  return {
    report: undefined,
    missing: [],
    failed: [],
    partial: false,
    nothingAt: null,
    readAt: null,
  };
}

/**
 * The oldest moment any statement on screen was read, so the freshness caption
 * can name the age of the page rather than the age of its last repaint. Null
 * when nothing was read.
 */
function oldestReadAt(statements: readonly LoadedStatement[]): number | null {
  let oldest: number | null = null;
  for (const statement of statements) {
    if (oldest === null || statement.readAt < oldest) oldest = statement.readAt;
  }
  return oldest;
}

/** The older of the current period's and the comparison column's own reads. */
function readTime(statement: LoadedStatement, comparison: CompareRead): number {
  const oldest = oldestReadAt([statement]);
  return compareReadTime(oldest, comparison);
}

/** The older of two read moments, either of which may be unknown. */
function compareReadTime(current: number | null, comparison: CompareRead): number {
  if (comparison.readAt === null) return current ?? Date.now();
  return current === null ? comparison.readAt : Math.min(current, comparison.readAt);
}

/**
 * Why a range's cash-flow figures do not foot. Closing cash is the last loaded
 * month's own figure (the books' figure); opening plus the change only reaches
 * it when every month in between is loaded. Said once, here.
 */
const CASH_GAP_TEXT =
  "A month in this period is not loaded yet, so opening cash plus the change does not reach the closing cash.";

/**
 * Why the opening cash is not the period's. Opening cash is the first loaded
 * month's own opening, so a first month that did not arrive moves it to the
 * next month — and the three headline figures still foot, which is exactly why
 * the gap sentence above cannot be the one to say it.
 */
const CASH_FIRST_MONTH_TEXT =
  "The first month in this period is not loaded yet, so the opening cash is the one the next loaded month began with.";

/** The same for the closing figure, which is the last loaded month's own. */
const CASH_LAST_MONTH_TEXT =
  "The last month in this period is not loaded yet, so the closing cash is the nearest loaded month's.";

/**
 * The one sentence for a Difference that is not printed because the period on
 * screen is short: a subtraction needs both sides to be whole. Names the months
 * the way the list above does, so the two cannot be read as different facts.
 */
function differenceNotShownSentence(scope: {
  missing: readonly string[];
  failed: readonly { period: string }[];
}): string {
  const items = [
    ...scope.missing.map((period) => missingMonthSentence(period)),
    ...scope.failed.map((item) => failedMonthSentence(item.period)),
  ];
  if (items.length === 0) return "";
  const last = items[items.length - 1] ?? "";
  const subject =
    items.length === 1 ? last : `${items.slice(0, -1).join(", ")} and ${last}`;
  return `${subject}, so the difference is not shown.`;
}

/** The comparison the dropdown names, read back into its own type. */
function compareChoiceOf(value: string): CompareChoice {
  if (value === "previous" || value === "same-last-year") return value;
  return "none";
}

/**
 * The arithmetic "the period before" uses for the shape on screen: a quarter
 * compares with the previous quarter, a year to date with the same months last
 * year, a month or a custom pair with the months immediately before.
 */
function compareBasisOf(range: ReportRange): CompareBasis {
  if (range === "quarter") return "quarter";
  if (range === "year") return "year";
  if (range === "month") return "month";
  return "custom";
}

/** The compact period words over the comparison column ("May 2026"). */
function compareColumnLabel(months: readonly string[]): string {
  const first = months[0];
  const last = months[months.length - 1];
  if (first === undefined || last === undefined) return "Comparison";
  if (months.length === 1) return monthText(first) || first;
  const from = shortMonthText(first);
  const to = shortMonthText(last);
  return first.slice(0, 4) === last.slice(0, 4)
    ? `${from} to ${to} ${first.slice(0, 4)}`
    : `${from} ${first.slice(0, 4)} to ${to} ${last.slice(0, 4)}`;
}

/** "Jan" from `2026-01`, never a token on the face. */
function shortMonthText(period: string): string {
  const month = Number(period.slice(5, 7));
  const name = MONTHS[month - 1];
  return name === undefined ? period : name.slice(0, 3);
}

/**
 * The one sentence above the tables that spells the comparison period out. A
 * balance sheet compares as of a day, every other statement over a span.
 *
 * The words follow the arithmetic, not the dropdown: "the period before" means
 * the previous quarter for a quarter and the same months last year for a year
 * to date, so the sentence says which of those it read. A period still in
 * progress is compared with whole months, and the sentence says so — the
 * difference is real, but it is not two equal periods.
 */
function compareSentence(
  months: readonly string[],
  report: ReportId,
  context: {
    basis: CompareBasis;
    choice: CompareChoice;
    partial: boolean;
    missing: readonly string[];
    /** The shape of a period that is still being lived in, or null. */
    periodInProgress: InProgress | null;
  },
): string {
  const first = months[0];
  if (first === undefined) return "Comparing with another period.";
  const last = months[months.length - 1] ?? first;
  const span =
    months.length === 1
      ? monthText(first) || first
      : `${monthText(first) || first} to ${monthText(last) || last}`;
  const short = context.partial
    ? ` ${context.missing.length === 1 ? "One month is" : "Some months are"} not loaded, so no difference is shown.`
    : "";
  if (report === "balance-sheet") {
    return `Comparing with ${formatShortDate(monthEndOf(first))}.${short}`;
  }
  const progress =
    context.periodInProgress === null
      ? ""
      : ` ${inProgressSentence(
          context.periodInProgress,
          context.choice === "same-last-year" || context.basis === "year",
        )}`;
  if (context.choice === "previous" && context.basis === "quarter") {
    return `Comparing with the previous quarter: ${span}.${progress}${short}`;
  }
  if (context.choice === "previous" && context.basis === "year") {
    return `Comparing with the same months last year: ${span}.${progress}${short}`;
  }
  return `Comparing with ${span}.${progress}${short}`;
}

/**
 * What kind of period is still being lived in: a this-month view, a quarter or
 * year to date reaching the clock month, or a custom range ending in it. Every
 * one of those is compared with months that are over, and the difference is
 * smaller than it will be on the last day of the month.
 */
type InProgress = "month" | "quarter" | "year" | "custom";

/** The shape a range on screen has, when its last month is the clock month. */
function periodInProgressOf(
  shape: ReportRange,
  months: readonly string[],
  clockMonth: string,
): InProgress | null {
  if (shape === "last-year") return null;
  const last = months[months.length - 1];
  return last === clockMonth ? shape : null;
}

/**
 * One sentence per shape: the period on the left is not over yet. What it is
 * compared WITH decides the second half — "the earlier quarter" when the
 * comparison is the period before, "the same months last year" when it is the
 * year-earlier one — or the sentence would name a period that is not there.
 */
function inProgressSentence(shape: InProgress, comparedLastYear: boolean): string {
  if (comparedLastYear) {
    if (shape === "month") {
      return "This month is not over yet, so the same month last year has more days.";
    }
    return `This ${shapeWord(shape)} is not over yet, so the same months last year have more days.`;
  }
  if (shape === "month") {
    return "This month is not over yet, so the earlier month has more days.";
  }
  if (shape === "quarter") {
    return "This quarter is not over yet, so the earlier quarter has more days.";
  }
  return "This period is not over yet, so the months it is compared with have more days.";
}

/** "quarter" / "year" / "period" — the word the sentence above needs. */
function shapeWord(shape: InProgress): string {
  if (shape === "quarter") return "quarter";
  if (shape === "year") return "year";
  if (shape === "month") return "month";
  return "period";
}

/**
 * What an empty period says (rd Y3). A period with nothing in it is not a
 * failure: it says so in one plain sentence, with no Try again button, because
 * trying again cannot change it.
 *
 * The sentence describes the period that was actually loaded, never the month
 * dropdown: a saved window (Last year, a specific month, custom dates) has its
 * own bounds, and `period` is only the dropdown's current value. So the copy is
 * built from the same start/end a person can see in the strip beside it.
 */
function emptyPeriodCopy(
  report: ReportId,
  bounds: { start: string; end: string },
  clockMonth: string,
): { status: ReportStatusKey; sentence: string } {
  const scope = periodScopeText(bounds, clockMonth);
  if (report === "balance-sheet") {
    return {
      status: "Nothing booked yet",
      sentence:
        scope.month === ""
          ? "Nothing has been booked yet."
          : `Nothing has been booked for ${scope.month} yet.`,
    };
  }
  return {
    status: "No income or expenses",
    sentence:
      scope.sentence === ""
        ? "No income or expenses in this period."
        : `No income or expenses ${scope.sentence}.`,
  };
}

/**
 * The words for the window a bound pair covers: one calendar month that is the
 * clock month reads `this month`; one other calendar month reads `in June 2025`;
 * anything wider says nothing, and the caller falls back to `in this period`.
 */
function periodScopeText(
  bounds: { start: string; end: string },
  clockMonth: string,
): { month: string; sentence: string } {
  const month = monthOf(bounds.start);
  if (month === "" || month !== monthOf(bounds.end)) {
    return { month: "", sentence: "" };
  }
  return {
    month: monthText(month),
    sentence: month === clockMonth ? "this month" : `in ${monthText(month)}`,
  };
}

/** `2026-06-30` -> `2026-06`; anything else -> "". */
function monthOf(iso: string): string {
  const match = /^(\d{4}-\d{2})-\d{2}$/.exec(iso);
  return match === null ? "" : (match[1] ?? "");
}

/**
 * One failed month, said plainly, beside the "not available yet" ones. A month
 * that failed is not missing: somebody can retry it, and the list says which —
 * with its year, because a range names months across years.
 */
function failedMonthSentence(period: string): string {
  return `${monthNameAndYear(period) || "That month"} could not be loaded`;
}

/** The same sentence for a month the comparison column needed. */
function failedComparisonMonthSentence(period: string): string {
  return `The comparison month ${monthNameAndYear(period) || "asked for"} could not be loaded`;
}

/** "October 2026" from `2026-10`; "" when the token is not a month. */
function monthNameAndYear(period: string): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return "";
  const name = MONTHS[Number(period.slice(5, 7)) - 1] ?? "";
  return name === "" ? "" : `${name} ${period.slice(0, 4)}`;
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function todayIso(now: Date = new Date()): string {
  return `${String(now.getFullYear())}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

function coverageForPeriod(period: string, report: ReportId, clockMonth: string): Coverage {
  const start = isValidPeriod(period) ? `${period}-01` : "";
  const end = !isValidPeriod(period)
    ? ""
    : period === clockMonth
      ? todayIso()
      : monthEndOf(period);
  return { start, end, asOf: report === "balance-sheet" };
}

/**
 * The bounds a scope names on screen. A custom pair is whole months, so its
 * end is that month's last day — or today inside the clock month, where the
 * books only reach as far as today.
 */
function coverageForScope(
  range: ReportRange,
  clockMonth: string,
  report: ReportId,
  custom: { start: string; end: string } | null,
): Coverage {
  if (range === "custom") {
    if (custom === null) return coverageForPeriod(clockMonth, report, clockMonth);
    const lastMonth = custom.end.slice(0, 7);
    const end = lastMonth === clockMonth ? todayIso() : monthEndOf(lastMonth);
    return { start: custom.start, end, asOf: report === "balance-sheet" };
  }
  if (range === "last-year") return lastYearCoverage(clockMonth);
  if (range === "month") return coverageForPeriod(clockMonth, report, clockMonth);
  return coverageForRange(range, clockMonth, report);
}

function coverageForRange(
  range: "month" | "quarter" | "year",
  clockMonth: string,
  report: ReportId,
): Coverage {
  if (range === "month") return coverageForPeriod(clockMonth, report, clockMonth);
  const year = Number(clockMonth.slice(0, 4));
  const month = Number(clockMonth.slice(5, 7));
  const startMonth = range === "quarter" ? Math.floor((month - 1) / 3) * 3 + 1 : 1;
  return {
    start: `${String(year)}-${pad2(startMonth)}-01`,
    end: todayIso(),
    asOf: report === "balance-sheet",
  };
}

function lastYearCoverage(clockMonth: string): Coverage {
  const year = Number(clockMonth.slice(0, 4)) - 1;
  return {
    start: `${String(year)}-01-01`,
    end: `${String(year)}-12-31`,
    asOf: false,
  };
}

function coverageForCustom(start: string, end: string, period: string): Coverage {
  if (isIsoDate(start) && isIsoDate(end)) return { start, end, asOf: false };
  return coverageForPeriod(period, "pnl", defaultPeriod());
}

function coverageForWindow(
  envelope: AdhocWindowEnvelope,
  period: string,
  report: ReportId,
): Coverage {
  const start = envelope.windowStart;
  const end = envelope.windowEnd;
  if (isIsoDate(start) && isIsoDate(end)) {
    return { start, end, asOf: report === "balance-sheet" };
  }
  return coverageForPeriod(period, report, defaultPeriod());
}

function coverageForStatement(
  report: ReportId,
  data: ReportDataMap[ReportId],
  statement: LoadedStatement,
  forPeriod: string,
  clockMonth: string,
): Coverage {
  if (report === "balance-sheet") {
    const asOf =
      statement.throughDate ??
      (typeof (data as BalanceSheetData).asOf === "string" ? (data as BalanceSheetData).asOf : "");
    const end = asOf !== "" ? asOf : coverageForPeriod(forPeriod, report, clockMonth).end;
    return { start: end, end, asOf: true };
  }
  const startBound =
    "periodStart" in data && typeof data.periodStart === "string" && data.periodStart !== ""
      ? data.periodStart
      : coverageForPeriod(forPeriod, report, clockMonth).start;
  const endBound =
    "periodEnd" in data && typeof data.periodEnd === "string" && data.periodEnd !== ""
      ? data.periodEnd
      : statement.throughDate ?? coverageForPeriod(forPeriod, report, clockMonth).end;
  return { start: startBound, end: endBound, asOf: false };
}

function coverageText(coverage: Coverage, report: ReportId): string {
  if (coverage.start === "" || coverage.end === "") return "Dates not available";
  if (report === "balance-sheet" || coverage.asOf) {
    return `As of ${formatShortDate(coverage.end)}`;
  }
  const sameYear = coverage.start.slice(0, 4) === coverage.end.slice(0, 4);
  const start = sameYear
    ? formatShortDate(coverage.start).replace(/,\s*\d{4}$/, "")
    : formatShortDate(coverage.start);
  return `${start} to ${formatShortDate(coverage.end)}`;
}

function landmarkName(report: ReportId, period: string): string {
  // A saved range window arrives as `2025-01:2025-08`; a screen reader should
  // hear the period, never the token.
  return `${REPORT_LANDMARK[report]} statement, ${windowText(period)}`;
}

interface LoadedStatement {
  data: ReportDataMap[ReportId];
  state: "open" | "closed";
  throughDate: string | null;
  asOf: string | null;
  generatedAt: string;
  /**
   * When this statement was read from the server (ms since the epoch). The
   * freshness caption is built from the OLDEST read behind the numbers on
   * screen, so a figure that came from this visit's memory is not called fresh.
   */
  readAt: number;
}

type ReportProblem =
  | { kind: "missing"; reason: LedgerNotPublishedReason | "unpublished" }
  | { kind: "status"; status: number }
  | { kind: "network" };

interface MonthRead {
  ok: true;
  statement: LoadedStatement;
}

interface MonthMiss {
  ok: false;
  problem: ReportProblem;
}

function problemFrom(error: unknown): ReportProblem {
  if (error instanceof ConsoleDataError) {
    if (error.status === 404) {
      return { kind: "missing", reason: notPublishedReason(error) ?? "unpublished" };
    }
    return { kind: "status", status: error.status };
  }
  return { kind: "network" };
}

interface ReportProblemCopy {
  text: string;
  retry: boolean;
  activity: boolean;
  unavailable: boolean;
  /** The sentence points at Connection status, so the control is offered. */
  connectionStatus: boolean;
}

function problemCopy(
  problem: ReportProblem,
  report: ReportId,
  period: string,
  clockMonth: string,
  role: StaffRole,
): ReportProblemCopy {
  const month = formatPeriodLabel(period);
  if (problem.kind === "missing") {
    if (problem.reason === "ledger_view_not_enabled") {
      // One wording per state (rd-polish-rules §1) and decision 2: the state
      // comes from the home Books uses, and neither role is sent to an
      // owner-only control that does not exist.
      return {
        text: `${BOOKS_VIEW_OFF_TEXT} ${advice("switch_on_books_view", role)}`,
        retry: false,
        activity: false,
        unavailable: true,
        connectionStatus: false,
      };
    }
    if (problem.reason === "statements_unavailable") {
      if (report === "by-location") {
        return {
          text: "By Location needs property locations set up for this company.",
          retry: false,
          activity: false,
          unavailable: true,
          connectionStatus: false,
        };
      }
      // The decided sentence for this state (rd-z-fixes-background §3, lanes Z4
      // and Z5), from its one home: a failed build offers Try again and a next
      // step, and the owner is never told to tell the owner.
      const owner = role === "owner";
      return {
        text: sentence("books_not_built", role, { month }),
        retry: true,
        activity: false,
        unavailable: true,
        connectionStatus: owner,
      };
    }
    // A snapshot that is not there (plain, or `ledger_not_published`) is a
    // month nobody has loaded — never a month with nothing booked (B9-H1).
    // Home says the same words for the same state; "This month" is said only
    // when the month on screen IS this month.
    return {
      text:
        period === clockMonth
          ? MONTH_NOT_LOADED_TEXT
          : `${month} has not been loaded yet.`,
      retry: false,
      activity: true,
      unavailable: true,
      connectionStatus: false,
    };
  }
  if (problem.kind === "status" && problem.status === 403) {
    return {
      text: "Reports are only available to staff.",
      retry: false,
      activity: false,
      unavailable: false,
      connectionStatus: false,
    };
  }
  if (problem.kind === "status" && problem.status === 401) {
    return {
      text: "Your sign-in expired. Reload the page.",
      retry: false,
      activity: false,
      unavailable: false,
      connectionStatus: false,
    };
  }
  if (problem.kind === "status" && problem.status === 429) {
    return {
      text: "Too many requests. Try again in a minute.",
      retry: true,
      activity: false,
      unavailable: false,
      connectionStatus: false,
    };
  }
  return {
    text: "Reports are unavailable right now. Try again.",
    retry: true,
    activity: false,
    unavailable: false,
    connectionStatus: false,
  };
}

function bandLines(statement: LoadedStatement): string[] {
  const notice = provisionalNoticeOf(statement.data);
  if (notice === null) return [];
  // The summary already says the month is not closed; do not say it twice.
  if (/^this month is not closed or reconciled\.?$/i.test(notice.trim())) return [];
  return [notice];
}

function periodRelation(period: string, clockMonth: string): "current" | "earlier" | "future" {
  if (period === clockMonth) return "current";
  return period < clockMonth ? "earlier" : "future";
}

function isLedgerReport(report: ReportId): report is LedgerStatementReport {
  return report !== "py-comparison";
}

async function readMonth(
  client: AdminClient,
  company: CompanySlug,
  report: ReportId,
  period: string,
  clockMonth: string,
): Promise<MonthRead | MonthMiss> {
  const relation = periodRelation(period, clockMonth);
  const readAt = Date.now();
  if (relation === "earlier" || report === "py-comparison") {
    try {
      const snapshot = await client.getReport(company, report, period);
      return {
        ok: true,
        statement: {
          data: snapshot.data,
          state: "closed",
          throughDate: null,
          asOf: null,
          generatedAt: snapshot.generatedAt,
          readAt,
        },
      };
    } catch (error) {
      const problem = problemFrom(error);
      if (problem.kind !== "missing" || report === "py-comparison") {
        return { ok: false, problem };
      }
    }
  }
  if (!isLedgerReport(report)) {
    return { ok: false, problem: { kind: "missing", reason: "unpublished" } };
  }
  try {
    const live = await client.getLedgerStatement(company, report, period);
    return { ok: true, statement: statementFromLedger(live, readAt) };
  } catch (error) {
    return { ok: false, problem: problemFrom(error) };
  }
}

/**
 * True for an answer that says "nobody has published this month" — the one
 * kind of miss worth remembering for the visit. A failed build or a refused
 * read is not: that is exactly the answer that may change.
 */
function isNotPublishedAnswer(read: MonthRead | MonthMiss): boolean {
  if (read.ok) return false;
  const problem = read.problem;
  if (problem.kind !== "missing") return false;
  return problem.reason === "unpublished" || problem.reason === "ledger_not_published";
}

function statementFromLedger(live: LedgerStatement, readAt: number): LoadedStatement {
  const data = live.data;
  if (!isReportData(data)) {
    return {
      data: { currency: "USD" } as ReportDataMap[ReportId],
      state: live.state,
      throughDate: live.throughDate,
      asOf: live.asOf,
      generatedAt: live.generatedAt,
      readAt,
    };
  }
  return {
    data,
    state: live.state,
    throughDate: live.throughDate,
    asOf: live.asOf,
    generatedAt: live.generatedAt,
    readAt,
  };
}

/** What one period's ledger header says, for every statement drawn from it. */
interface LedgerContext {
  loaderErrors: number;
  firstPublished: string | null;
  closings: Map<string, string>;
}

/**
 * The books' own answer about a period.
 *
 * `read`: the header arrived. `unpublished`: there is no header for that month
 * (404) — an answer, remembered for the visit like a month nobody has
 * published. `failed`: no answer at all (a 429, a 500, a lost connection) —
 * never remembered, and never painted as though the ledger were clean.
 */
type BooksContext =
  | { kind: "read"; context: LedgerContext }
  | { kind: "unpublished" }
  | { kind: "failed" };

async function readLedgerContext(
  client: AdminClient,
  company: CompanySlug,
  period: string,
): Promise<BooksContext> {
  // No queue count here: the role-free header field is not a number anyone may
  // print (item B4b-1), and this page's read budget cannot take a queue read.
  try {
    const page: LedgerAccountsResponse = await client.getLedgerAccounts(company, period);
    let first: string | null = null;
    for (const row of page.header.periods) {
      if (first === null || row.period < first) first = row.period;
    }
    const closings = new Map<string, string>();
    for (const account of page.accounts) closings.set(account.account, account.closing);
    return {
      kind: "read",
      context: {
        loaderErrors: page.header.loaderErrors,
        firstPublished: first,
        closings,
      },
    };
  } catch (error) {
    // A 404 is "there is no ledger header for this month", which will keep
    // being true until someone publishes it. Anything else is a request that
    // did not answer, and the caller must ask again rather than settle.
    return problemFrom(error).kind === "missing" ? { kind: "unpublished" } : { kind: "failed" };
  }
}

function toSnapshot<K extends ReportId>(
  company: CompanySlug,
  report: K,
  period: string,
  statement: LoadedStatement,
): ReportSnapshot<ReportDataMap[K]> {
  return {
    apiVersion: "v1",
    company,
    report,
    period,
    generatedAt: statement.generatedAt,
    pipelineRunId: "statement",
    data: statement.data as ReportDataMap[K],
  };
}

function isReportData(data: unknown): data is ReportDataMap[ReportId] {
  return typeof data === "object" && data !== null;
}

function isPnlData(data: unknown): data is PnlData {
  if (typeof data !== "object" || data === null) return false;
  const row = data as PnlData;
  return Array.isArray(row.sections) && typeof row.netIncome === "string";
}

function isBlankPayload(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return true;
  return Object.keys(data).every((key) => key === "currency");
}

function statementReady<K extends ReportId>(
  report: K,
  data: ReportDataMap[K],
): data is ReportDataMap[K] {
  if (report === "pnl") return isPnlData(data);
  if (report === "balance-sheet") {
    const row = data as BalanceSheetData;
    return Array.isArray(row.sections) && typeof row.asOf === "string";
  }
  if (report === "cash-flow") {
    const row = data as CashFlowData;
    return (
      Array.isArray(row.sections) &&
      typeof row.openingCash === "string" &&
      typeof row.closingCash === "string" &&
      typeof row.netChange === "string"
    );
  }
  if (report === "by-location") {
    const row = data as ByLocationData;
    return Array.isArray(row.locations);
  }
  if (report === "py-comparison") {
    const row = data as PyComparisonData;
    return typeof row.available === "boolean" && Array.isArray(row.lines);
  }
  return false;
}

function safeFile(value: string): string {
  const cleaned = value.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return cleaned === "" ? "report" : cleaned;
}
